/*
 * VetPets Subscription Portal — UI controller
 * ------------------------------------------------------------------
 * Owns navigation, rendering, sheets, pending/toast/error states and
 * focus management. It never fetches and never formats currency on its
 * own — everything comes from the adapter, so replacing the mock with
 * the live backend requires no change in this file.
 *
 * Scope is the Phoenix-supported surface: skip, delay/reschedule, cancel
 * and reactivate. Quantity, swap, one-time item, address, card and
 * frequency/pause/resume are not portal capabilities and have no code
 * path here.
 */
(function (window, document) {
  'use strict';

  var NS = window.VetPetsPortal;
  if (!NS) return;

  var SCREENS_WITH_CHROME = {
    login: false, sent: false, expired: false, loading: false, error: false
  };

  /**
   * Failures that mean "you are not signed in", not "your subscription could
   * not be loaded".
   *
   * These must always land on the clean sign-in screen. Rendering them as the
   * error screen produced a SUB-503 reference for what was simply an expired
   * or absent session — a support code pointing at Phoenix for a problem one
   * tap could fix, and a customer with no way to fix it.
   */
  var AUTH_FAILURE_CODES = {
    unauthenticated: true,
    expired_link: true,
    no_subscription: true
  };

  function Portal(root) {
    this.root = root;
    this.cfg = this.readConfig(root);

    // createAdapter refuses to hand back mock data in live mode, so a
    // fabricated VetPoints balance can never reach a real customer.
    try {
      this.adapter = NS.createAdapter({
        mode: this.cfg.mode,
        basePath: this.cfg.basePath,
        // Live counts from the real calendar day. `cfg.today` is the
        // prototype's frozen date (2026-08-21) and may only reach the mock
        // adapter — it is what rendered "34 days away" for a delivery 25 days
        // out. Local date, not UTC: a customer counts sleeps, not hours.
        today: this.cfg.mode === 'live' ? NS.dates.toISO(new Date()) : this.cfg.today,
        currencyCode: this.cfg.currency,
        latency: this.cfg.latency,
        pointsPerRenewal: this.cfg.pointsPerRenewal,
        nextRewardAt: this.cfg.nextRewardAt,
        nextRewardName: this.cfg.nextRewardName,
        images: this.cfg.images
      });
    } catch (e) {
      this.adapter = null;
      this.bootError = e;
    }

    this.state = {
      screen: 'loading',
      sheet: null,
      pending: null,
      lastFocus: null,
      // startedCategory: a soft, non-blocking answer to "why did you start"
      // (cancel-started screen) — captured for the UI's own selection state
      // only. It is never sent to Phoenix and never personalizes anything
      // else; see cancel-started in spp-screen-cancel.liquid.
      draft: { delay: 7, reason: null, restart: 0, date: null, note: '', gap: null, startedCategory: null,
        // Step 6 "Adjust your routine": which secondary adjustment is open, the
        // chosen tile (7/15/30 days, or 1/2/3 months for a pause) and the resume date.
        retainAdj: null, retainTile: null, retainDate: '' },
      retainError: null,
      reasonError: null,
      // The one journey id for this cancellation attempt, adopted from
      // whichever call — the cancellation_started beacon or the reason
      // write itself — resolves first. Reused by every later screen-view
      // beacon and by a gap-save mutation, so they all land on the SAME
      // journey rather than each minting their own.
      retentionJourneyId: null,
      // Set only when saving the reason exhausted its retries. Distinct
      // from reasonError (a VALIDATION message, cleared as soon as the
      // problem is fixed): this is a delivery failure with a valid
      // selection already in hand, and stays visible until the customer
      // retries or picks again.
      reasonSaveError: null,
      data: null,
      loyalty: null,
      loyaltyPending: false,
      customer: null,
      inactive: [],
      deliveries: null,
      rewards: [],
      error: null,
      success: null,
      history: []
    };

    this.bind();
    this.boot();
  }

  /* =================================================================
   * Config
   * ================================================================= */

  Portal.prototype.readConfig = function (root) {
    var d = root.dataset;
    return {
      mode: d.sppMode === 'live' ? 'live' : 'mock',
      basePath: d.sppBasePath || '/apps/subscriptions',
      locale: d.sppLocale || 'en',
      today: d.sppToday || '2026-08-21',
      currency: d.sppCurrency || 'USD',
      latency: parseInt(d.sppLatency, 10) || 0,
      pointsPerRenewal: parseInt(d.sppPointsPerRenewal, 10) || 100,
      nextRewardAt: parseInt(d.sppNextRewardAt, 10) || 800,
      nextRewardName: d.sppNextRewardName || 'Free Surprise Gift',
      images: {
        freshwipes: d.sppImgFreshwipes || '',
        eyewipes: d.sppImgEyewipes || '',
        glovewipes: d.sppImgGlovewipes || '',
        reward: ''
      },
      devDefault: d.sppDevDefault === 'true'
    };
  };

  /* =================================================================
   * Formatting (locale aware — never hardcodes a symbol)
   * ================================================================= */

  Portal.prototype.fmtMoney = function (value) { return NS.formatMoney(value, this.cfg.locale); };

  /**
   * The window the BACKEND will accept for a rescheduled delivery.
   *
   * Mirrored from the route rather than invented here: it refuses a date
   * before today or more than a year out. Both bounds are computed in UTC,
   * exactly as the server does, so the picker can never offer a date the
   * server would then reject — a customer choosing a date and being told no
   * is a worse experience than not being offered it.
   */
  var MAX_RESCHEDULE_DAYS = 365;

  Portal.prototype.rescheduleBounds = function () {
    var today = new Date().toISOString().slice(0, 10);
    return { min: today, max: NS.dates.addDays(today, MAX_RESCHEDULE_DAYS) };
  };

  /** Is the custom-date option the one currently chosen? */
  Portal.prototype.isCustomDate = function () {
    return this.state.draft.delay === 'custom';
  };

  /**
   * The date this sheet would move the delivery to, or null when the customer
   * has chosen "another date" and not yet picked one.
   */
  Portal.prototype.delayTargetIso = function () {
    var sub = this.state.data;
    if (!sub || !sub.nextOrderDate) return null;
    if (this.isCustomDate()) return this.state.draft.date || null;
    return NS.dates.addDays(sub.nextOrderDate, this.state.draft.delay);
  };

  /**
   * Why the chosen date cannot be submitted, or null when it can.
   *
   * Checked here as well as on the server. The server is the authority — this
   * exists so the customer is told immediately and in their own terms, not
   * after a round trip that reads like a failure.
   */
  Portal.prototype.rescheduleError = function () {
    if (!this.isCustomDate()) return null;

    var sub = this.state.data;
    var chosen = this.state.draft.date;
    if (!chosen) return 'Choose a date for your next delivery.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(chosen)) return 'That date could not be read.';

    var b = this.rescheduleBounds();
    if (chosen < b.min) return 'Choose a date from today onwards.';
    if (chosen > b.max) return 'Choose a date within the next year.';
    if (sub && chosen === sub.nextOrderDate) return 'That is already your delivery date.';
    return null;
  };

  Portal.prototype.fmtDate = function (iso, style) {
    if (!iso) return '';
    var date = NS.dates.parseISO(iso);
    var opts;
    if (style === 'long') opts = { weekday: 'long', month: 'long', day: 'numeric' };
    else if (style === 'medium') opts = { weekday: 'short', month: 'short', day: 'numeric' };
    else if (style === 'short') opts = { month: 'short', day: 'numeric' };
    else if (style === 'full') opts = { day: 'numeric', month: 'long', year: 'numeric' };
    else opts = { month: 'short', day: 'numeric' };
    try { return new Intl.DateTimeFormat(this.cfg.locale, opts).format(date); }
    catch (e) { return iso; }
  };

  /* =================================================================
   * Boot
   * ================================================================= */

  Portal.prototype.boot = function () {
    var self = this;
    var params = new URLSearchParams(window.location.search);

    // Review tools are mock-mode only — never expose them in production.
    // spp_dev=1 reveals the switcher; it can never switch the data mode.
    if ((params.get('spp_dev') === '1' || this.cfg.devDefault) && this.cfg.mode === 'mock') {
      this.root.classList.add('is-dev');
      var dev = this.root.querySelector('[data-spp-dev]');
      if (dev) dev.hidden = false;
    }

    // In live mode, wipe every static design placeholder before anything
    // renders. The markup ships with prototype values ("640" VetPoints,
    // "$62.73", "Visa ···· 4242") purely so the design reviews without JS.
    // A real customer must never see a fabricated number, not even briefly
    // and not merely hidden behind another screen.
    if (this.cfg.mode === 'live') this.clearPlaceholders();

    this.show('loading');

    if (!this.adapter) { this.fail(this.bootError); return; }

    var start = this.cfg.mode === 'mock' ? params.get('spp_screen') : null;

    if (this.cfg.mode === 'live') { this.bootLive(start); return; }

    var token = params.get('spp_token');

    if (token) {
      // A FRESH magic-link authentication — lands on the welcome screen
      // once, per the approved journey, never straight on the dashboard.
      // See bootLive()'s handoff branch for the live-mode equivalent.
      this.adapter.verifyMagicLink(token)
        .then(function () { return self.load(); })
        .then(function () { self.show(start || 'welcome'); })
        .catch(function (err) {
          if (err && err.code === 'expired_link') self.show('expired');
          else self.fail(err);
        });
      return;
    }

    // A returning, already-authenticated visit (reload, deep link) — the
    // welcome screen has already been shown once this session; skip
    // straight to the dashboard, exactly as before.
    this.load()
      .then(function () { self.show(start || 'dashboard'); })
      .catch(function (err) { self.fail(err); });
  };

  /**
   * Live boot — the cookie-free handoff.
   *
   * Order is load-bearing. The handoff code is taken out of the address bar
   * FIRST, before any await and before anything else on the page can read
   * `location.search`, because a URL reaches history, `Referer` and analytics.
   * Only then is it exchanged for the session.
   *
   * Three entry paths:
   *   - arriving from the emailed link, carrying a handoff;
   *   - reloading the tab with a session already in sessionStorage;
   *   - arriving cold, which is the sign-in screen.
   */
  Portal.prototype.bootLive = function (start) {
    var self = this;

    // Synchronous and first. Nothing may await before this returns.
    var handoff = NS.takeHandoffFromUrl();

    function loadAndShow(target) {
      return self.load().then(function () { self.show(start || target); });
    }

    // One handler, shared with retry and every action: fail() itself decides
    // sign-in versus error screen. Keeping that rule in a single place is what
    // stops the two paths from disagreeing.
    function onFailure(err) {
      self.fail(err);
    }

    if (handoff) {
      // A FRESH authentication — the handoff only exists once, the moment
      // the emailed link is exchanged for a session. That is the one event
      // the approved welcome screen exists for; every later load of this
      // tab (below) carries no handoff and goes straight to the dashboard.
      this.adapter.exchangeHandoff(handoff)
        .then(function () { return loadAndShow('welcome'); })
        .catch(onFailure);
      return;
    }

    // A reload in the same tab reuses the unexpired session; closing the tab
    // ends it, because sessionStorage does. Not a fresh authentication, so
    // the welcome screen does not reappear.
    if (this.adapter.hasSession && this.adapter.hasSession()) {
      loadAndShow('dashboard').catch(onFailure);
      return;
    }

    this.show('login');
  };

  /**
   * Blank every data slot in the document, including the ones inside list
   * <template> elements, so no prototype value survives into production.
   */
  Portal.prototype.clearPlaceholders = function () {
    var i;
    var slots = this.root.querySelectorAll('[data-spp-field], [data-spp-field-html]');
    for (i = 0; i < slots.length; i++) slots[i].textContent = '';

    var tpls = this.root.querySelectorAll('template[data-spp-tpl]');
    for (i = 0; i < tpls.length; i++) {
      var inner = tpls[i].content.querySelectorAll('[data-spp-field], [data-spp-field-html]');
      for (var j = 0; j < inner.length; j++) inner[j].textContent = '';
    }

    // Form fields, which are NOT [data-spp-field] slots and so survived the
    // loop above. The sign-in input once shipped with a prototype address in
    // its `value`, and a real customer had to delete a stranger's email before
    // typing their own. The markup no longer carries one; this makes it
    // impossible for any future markup to reintroduce it in live mode.
    var inputs = this.root.querySelectorAll('input[type="email"], input[type="text"], textarea');
    for (i = 0; i < inputs.length; i++) {
      if (inputs[i].value) inputs[i].value = '';
      inputs[i].removeAttribute('value');
    }

    // Prototype affordances. Removed outright rather than hidden: a control a
    // real customer must never reach should not exist in the document.
    var mockOnly = this.root.querySelectorAll('[data-spp-mock-only]');
    for (i = 0; i < mockOnly.length; i++) {
      if (mockOnly[i].parentNode) mockOnly[i].parentNode.removeChild(mockOnly[i]);
    }
  };

  /**
   * Is there anything to read the portal WITH?
   *
   * In live mode every portal read needs a session. Asking without one is not
   * a server error to be reported — it is the sign-in screen.
   */
  Portal.prototype.hasSession = function () {
    if (this.cfg.mode !== 'live') return true;
    return !!(this.adapter && this.adapter.hasSession && this.adapter.hasSession());
  };

  /** Drop whatever this browser is holding. Local only; no request. */
  Portal.prototype.clearSessionLocally = function () {
    if (this.adapter && this.adapter.clearSession) {
      try { this.adapter.clearSession(); } catch (e) { /* nothing to clear */ }
    }
  };

  Portal.prototype.load = function () {
    var self = this;

    // Guard the whole authenticated surface in one place, so no caller can
    // reach it before a handoff has been exchanged.
    if (!this.hasSession()) {
      return Promise.reject(NS.PortalError('unauthenticated', 'Sign in to view your subscription.'));
    }

    return Promise.all([
      this.adapter.getCustomer(),
      this.adapter.getSubscription(),
      this.adapter.getLoyalty(),
      this.adapter.listSubscriptions(),
      this.adapter.listDeliveries(),
      this.adapter.listRewards()
    ]).then(function (r) {
      self.state.customer = r[0];
      self.state.data = r[1];
      self.state.loyalty = r[2];
      self.state.inactive = r[3].inactive;
      self.state.deliveries = r[4];
      self.state.rewards = r[5];
      self.render();
    });
  };

  /**
   * The ONE place a failure becomes a screen.
   *
   * Boot, retry and every action funnel through here, so the rule cannot be
   * true on one path and false on another — which is exactly how a 401 came to
   * be rendered as SUB-503 from the retry button but as the login screen from
   * boot.
   */
  Portal.prototype.fail = function (err) {
    if (err && AUTH_FAILURE_CODES[err.code]) {
      // Not signed in. Drop the dead token so the next attempt starts clean,
      // and show sign-in rather than an error the customer cannot act on.
      this.clearSessionLocally();
      this.state.error = null;
      this.state.pending = null;
      this.show('login');
      return;
    }

    this.state.error = {
      code: (err && err.code) || 'server',
      reference: (err && err.reference) || this.buildReference(err)
    };
    this.render();
    this.show('error');
  };

  /**
   * A support reference for the error screen.
   *
   * In live mode the stamp is the REAL current date. `cfg.today` is the
   * prototype's frozen date and belongs to mock mode only — stamping it on a
   * real customer's error produced a reference dated August 21, 2026 no matter
   * when the failure happened, which is worse than useless to support.
   *
   * The code is a coarse class, never the upstream message: the server
   * deliberately withholds that, and repeating a guess here would undo it.
   */
  Portal.prototype.buildReference = function (err) {
    var code = (err && err.code) || 'server';
    var stamp = this.cfg.mode === 'live'
      ? this.fmtDate(NS.dates.toISO(new Date()), 'full')
      : this.fmtDate(this.cfg.today, 'full');

    // SUB-503 is reserved for a genuine failure of an AUTHENTICATED read.
    // fail() already routes auth failures to sign-in, so this branch should be
    // unreachable — it exists so that if a new path ever forgets, the customer
    // and support see an authentication code rather than a Phoenix one.
    var prefix;
    if (AUTH_FAILURE_CODES[code]) prefix = 'SUB-AUTH';
    else if (code === 'network') prefix = 'SUB-000';
    else if (code === 'mock_in_production') prefix = 'SUB-CFG';
    else prefix = 'SUB-503';

    return prefix + ' · ' + stamp;
  };

  /* =================================================================
   * Navigation
   * ================================================================= */

  /**
   * VETPOINTS IS LIVE.
   *
   * The ledger now exists: `getLoyalty()` reads a real balance and history
   * from POST /portal/vetpoints, session-authenticated exactly like every
   * other portal read. Nothing here fabricates a number — a customer with no
   * ledger entry yet sees zero, not a placeholder.
   *
   * The reward catalogue and redemption request are still out of scope: the
   * screen shows the balance, the progress toward the configured threshold,
   * and the transaction history, and nothing more.
   *
   * Flipping this back to false re-hides every [data-spp-loyalty] element and
   * makes the loyalty screen unreachable again — kept as an emergency switch
   * if the ledger ever needs to come back down without a code review.
   */
  var LOYALTY_ENABLED = true;

  /** Screens a customer must not reach while their feature is off. */
  var DISABLED_SCREENS = LOYALTY_ENABLED ? {} : { loyalty: 1 };

  Portal.prototype.applyLoyaltyVisibility = function () {
    var nodes = this.root.querySelectorAll('[data-spp-loyalty]');
    for (var i = 0; i < nodes.length; i++) nodes[i].hidden = !LOYALTY_ENABLED;
  };

  Portal.prototype.show = function (screen) {
    // A hidden feature must be unreachable, not merely unlinked. Deep links,
    // history and stray data-spp-go values all arrive here.
    if (DISABLED_SCREENS[screen]) screen = 'dashboard';

    /* The 40% offer is one-time per customer, permanently, and the SERVER
     * decides that — this only keeps the customer from ever being shown an
     * offer the server has already refused. Checked here, not just on the
     * one button that used to link to it, so a deep link, browser back/
     * forward, or a future caller cannot resurface it either. Applies for
     * every cancellation reason now that the offer has its own screen
     * (cancel-savings), not just whichever reason used to earn it. */
    if (screen === 'cancel-savings' && this.state.data && this.state.data.retentionOfferRedeemed) {
      screen = 'cancel-confirm';
    }

    if (this.state.screen !== screen) this.state.history.push(this.state.screen);
    this.state.screen = screen;
    this.closeSheet(true);
    this.beaconScreenView(screen);

    /* ONE CONFIRMATION, AT MOST ONE MUTATION — re-armed here.
     *
     * THE DEFECT THIS LINE EXISTS FOR
     * -------------------------------
     * The guard was originally re-armed only by openSheet(), because skip and
     * delay are sheets. Cancel and reactivate are not: they are SCREENS,
     * reached with data-spp-go. So after any sheet mutation the flag stayed
     * spent, and every later press of "Yes, cancel my subscription" hit the
     * guard and returned — no request, no spinner, no error, nothing. The
     * button was dead, and looked it.
     *
     * Arriving at a new screen is a fresh confirmation exactly as opening a
     * sheet is. Navigation is the customer asking again.
     */
    this.state.confirmSpent = false;
    // A step-6/step-7 failure message belongs to the attempt that produced it.
    this.state.retainError = null;
    this.state.offerError = null;

    var sections = this.root.querySelectorAll('[data-spp-screen]');
    for (var i = 0; i < sections.length; i++) {
      sections[i].hidden = sections[i].getAttribute('data-spp-screen') !== screen;
    }

    var showChrome = SCREENS_WITH_CHROME[screen] !== false;
    var chrome = this.root.querySelectorAll('[data-spp-chrome]');
    for (var j = 0; j < chrome.length; j++) chrome[j].hidden = !showChrome;

    this.markCurrentNav(screen);
    this.render();

    var main = this.root.querySelector('#spp-main');
    if (main) {
      main.scrollTop = 0;
      var heading = this.root.querySelector('[data-spp-screen="' + screen + '"]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
      }
    }
  };

  /**
   * Retention Command Center: "the customer looked at this screen."
   *
   * Fire-and-forget, exactly like recordCancelReason/recordCancelOutcome
   * already are — telemetry must never block or slow navigation. Called
   * with the FINAL resolved screen (after the already-redeemed redirect
   * above has run), so a redeemed customer who never actually sees
   * cancel-savings never triggers offer_shown for it — they land straight
   * on cancel-confirm, which fires final_confirmation_reached instead.
   *
   * Server-side dedup ((journey_id, event_type) is unique) means firing
   * this on every re-render of the same screen — a refresh, a browser
   * back/forward, revisiting — can never double-count a step; nothing
   * client-side needs to guard against that.
   *
   * The keys here are the four screens this ties to Retention Command
   * Center's funnel, by NAME, not by their position in the approved
   * Portal V2 order — cancel-reason now sits at step 5, after cancel-alt's
   * step 4, but each event still means exactly what its name says
   * regardless of when the customer reaches it. offer_shown now names
   * cancel-savings (step 7), the one standalone offer screen, rather than
   * cancel-offer (step 6, "Adjust your routine"), which no longer shows
   * the offer at all.
   *
   * cancellation_started opens the journey on step 1 (cancel-choose), the
   * flow's only entry: every customer who enters the cancel flow is counted,
   * whichever step they leave on — founder decision 2026-10-12 ("in the
   * flow and did not cancel" is a save). cancel-reason keeps it too, as the
   * fallback if the step-1 beacon failed; with a journey already held it
   * reuses that one and the server dedups the event.
   */
  var RETENTION_SCREEN_EVENTS = {
    'cancel-choose': 'cancellation_started',
    'cancel-reason': 'cancellation_started',
    'cancel-alt': 'longer_gap_reached',
    'cancel-savings': 'offer_shown',
    'cancel-confirm': 'final_confirmation_reached'
  };

  Portal.prototype.beaconScreenView = function (screen) {
    var self = this;
    var eventType = RETENTION_SCREEN_EVENTS[screen];

    /* Additive tracking, independent of the named RCC funnel above:
     * cancel-offer's reason-personalized recommendation (whatever kind it
     * is now — never 'offer') and cancel-savings' fixed offer
     * recommendation are both reported through recommendation_shown,
     * typed by action key. */
    var rec = null;
    if (screen === 'cancel-offer') {
      rec = self.retainKind();
    } else if (screen === 'cancel-savings') {
      rec = { kind: 'offer', action: 'next_delivery_40' };
    }

    // Sent with no journey id, every event except cancellation_started is
    // attached server-side to the customer's newest OPEN journey — which can
    // be a stale one from an earlier visit (e.g. cancel-alt, step 4, is
    // reached before cancel-reason opens this visit's journey). Only
    // cancellation_started may go out without one: it always opens a fresh
    // journey. Everything else waits for this visit's own.
    if (eventType && self.adapter.recordRetentionEvent &&
        (eventType === 'cancellation_started' || self.state.retentionJourneyId)) {
      self.adapter.recordRetentionEvent(eventType, self.state.retentionJourneyId, 'next_delivery_40')
        .then(function (result) {
          // Adopt the journey id only if we did not already have one — the
          // reason write (or an earlier beacon) always wins if it got there
          // first, so every event for this attempt lands on one journey.
          if (result && result.journeyId && !self.state.retentionJourneyId) {
            self.state.retentionJourneyId = result.journeyId;
          }
        })
        .catch(function () {});
    }
    if (rec) self.trackRetention('recommendation_shown', rec.action);
  };

  Portal.prototype.markCurrentNav = function (screen) {
    var navs = this.root.querySelectorAll('[data-spp-nav]');
    for (var i = 0; i < navs.length; i++) {
      if (navs[i].getAttribute('data-spp-nav') === screen) navs[i].setAttribute('aria-current', 'page');
      else navs[i].removeAttribute('aria-current');
    }
    var devBtns = this.root.querySelectorAll('[data-spp-dev] [data-spp-go]');
    for (var k = 0; k < devBtns.length; k++) {
      if (devBtns[k].getAttribute('data-spp-go') === screen) devBtns[k].setAttribute('aria-current', 'true');
      else devBtns[k].removeAttribute('aria-current');
    }
  };

  /* =================================================================
   * Sheets — skip and delay only
   * ================================================================= */

  Portal.prototype.openSheet = function (name) {
    var overlay = this.root.querySelector('[data-spp-overlay]');
    var host = this.root.querySelector('[data-spp-sheet-host]');
    if (!overlay || !host) return;
    if (name !== 'skip' && name !== 'delay') return;

    this.state.lastFocus = document.activeElement;
    this.state.sheet = name;

    // Opening the sheet is a fresh confirmation. See show() for the other
    // way a customer arrives at one.
    this.state.confirmSpent = false;

    var panels = host.querySelectorAll('[data-spp-sheet-panel]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-spp-sheet-panel') !== name;
    }
    host.setAttribute('aria-labelledby', 'spp-sheet-h-' + name);
    overlay.hidden = false;

    this.render();

    var focusable = this.focusablesIn(host);
    if (focusable.length) focusable[0].focus();
    else host.focus();
  };

  Portal.prototype.closeSheet = function (silent) {
    var overlay = this.root.querySelector('[data-spp-overlay]');
    if (!overlay || overlay.hidden) { this.state.sheet = null; return; }
    overlay.hidden = true;
    this.state.sheet = null;
    var host = this.root.querySelector('[data-spp-sheet-host]');
    if (host) host.classList.remove('is-pending');
    var note = this.root.querySelector('[data-spp-pending-note]');
    if (note) note.hidden = true;
    if (!silent && this.state.lastFocus && this.state.lastFocus.focus) {
      try { this.state.lastFocus.focus(); } catch (e) {}
    }
    this.state.lastFocus = null;
  };

  Portal.prototype.focusablesIn = function (el) {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(el.querySelectorAll(sel), function (n) {
      return n.offsetParent !== null || n === document.activeElement;
    });
  };

  /* =================================================================
   * Toast
   * ================================================================= */

  Portal.prototype.toast = function (message) {
    var el = this.root.querySelector('[data-spp-toast]');
    if (!el || !message) return;
    var slot = el.querySelector('[data-spp-field="toast.message"]');
    if (slot) slot.textContent = message;
    el.hidden = false;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () { el.hidden = true; }, 3600);
  };

  /* =================================================================
   * Mutation runner
   * ================================================================= */

  /**
   * Outcomes that leave the result genuinely UNKNOWN.
   *
   * After one of these the attempt key is KEPT, so that if the customer tries
   * again the server recognises the retry and replays it instead of issuing a
   * second write. Every other outcome is definitive and releases the key.
   */
  var INDETERMINATE = { timeout: 1, network: 1 };

  /**
   * One key for one logical attempt.
   *
   * The key identifies the customer's INTENTION, not the HTTP request that
   * carries it. Minting a fresh key per call is what allowed one confirmation
   * to become two independently keyed skips: the server saw two unrelated
   * operations because, as far as the keys were concerned, they were.
   *
   * The same key therefore survives a rerender, a repeated handler, a retry
   * and a delayed interaction. It is released only when the server has given a
   * definitive answer, or when fresh portal state has been loaded.
   */
  Portal.prototype.attemptKey = function (op) {
    if (!this.state.attempts) this.state.attempts = {};
    if (!this.state.attempts[op]) {
      var random = 'xxxxxxxxxxxx'.replace(/x/g, function () {
        return Math.floor(Math.random() * 16).toString(16);
      });
      this.state.attempts[op] = 'vp-' + Date.now().toString(36) + '-' + random;
    }
    return this.state.attempts[op];
  };

  /** Definitive answer received: the next attempt is a new intention. */
  Portal.prototype.releaseAttempt = function (op) {
    if (this.state.attempts) delete this.state.attempts[op];
  };

  /** Fresh state loaded: nothing in flight can still be meaningful. */
  Portal.prototype.releaseAllAttempts = function () {
    this.state.attempts = {};
  };

  Portal.prototype.run = function (key, work, opts) {
    var self = this;
    opts = opts || {};
    if (this.state.pending) return Promise.resolve();

    // Synchronous, before a single await: by the time any other handler can
    // run, the controls are disabled and the guard is set.
    this.state.pending = key;
    this.applyPending(true);
    this.render();

    // `attempt` names the logical operation for keyed actions. work() receives
    // the key so every call for one intention carries the same one.
    var op = opts.attempt || null;
    var attemptKey = op ? this.attemptKey(op) : null;

    return work(attemptKey)
      .then(function (result) {
        self.state.pending = null;
        self.applyPending(false);
        // The server answered. Whatever the customer does next is a new
        // intention and deserves a new key.
        if (op) self.releaseAttempt(op);

        if (result && result.id) self.state.data = result;

        // THE BUG THIS GUARD EXISTS FOR:
        //
        // run() was written for subscription mutations, which must re-read the
        // subscription afterwards. `sendLink` and `resend` also use run(), so a
        // SUCCESSFUL request for a magic link immediately performed three
        // authenticated portal reads — before any link had been opened, before
        // any handoff existed and before any session existed. The reads failed
        // as `unauthenticated`, the catch below called fail(), and the customer
        // saw SUB-503 instead of "Check your inbox".
        //
        // Authentication actions pass refresh:false. There is nothing to
        // refresh: by definition there is no session yet.
        var refreshed = opts.refresh === false
          ? Promise.resolve()
          : self.refreshAuthenticatedData();

        return refreshed.then(function () {
          // The sheet always closes on success, before anything renders.
          if (opts.closeSheet !== false) self.closeSheet();

          // The write applied but the server could not re-read Phoenix. The
          // change is REAL, so it is never reported as a failure — the
          // customer is told it worked and asked to refresh, rather than told
          // nothing happened and tempted to do it twice.
          if (result && result.refreshRequired) {
            self.render();
            self.refreshRequired();
            return;
          }

          if (opts.success) {
            self.state.success = opts.success(self.state, result);
            self.show('success');
          } else if (opts.then) {
            opts.then(self.state, result);
          } else {
            self.render();
          }
          if (opts.toast) self.toast(typeof opts.toast === 'function' ? opts.toast(self.state, result) : opts.toast);
        });
      })
      .catch(function (err) {
        self.state.pending = null;
        self.applyPending(false);
        self.closeSheet(true);

        // A definitive refusal frees the key. A timeout or a dropped
        // connection does NOT: the operation may well have applied, so a
        // retry has to arrive under the same key for the server to recognise
        // it rather than perform the change a second time.
        //
        // The same rule re-arms the confirmation. Nothing was applied, so the
        // customer is entitled to try again on the screen they are already
        // looking at — without it, a single network blip would leave them
        // pressing a button that silently refuses, which is the failure this
        // whole guard was rewritten to prevent.
        if (op && !INDETERMINATE[err && err.code]) {
          self.releaseAttempt(op);
          self.state.confirmSpent = false;
        }

        // A FAILED ACTION IS NOT A FAILED PAGE.
        //
        // This used to call fail(), which replaced the whole dashboard with
        // the full-page error screen. The first real skip did exactly that:
        // the customer confirmed, the operation failed, and their entire
        // subscription vanished behind "We couldn't load your subscription /
        // SUB-503" — even though the subscription had loaded perfectly a
        // moment earlier and nothing about it had changed.
        //
        // The dashboard stays. Only an authentication failure may take over
        // the page, because then there is genuinely nothing to show.
        if (err && AUTH_FAILURE_CODES[err.code]) {
          self.fail(err);
          return;
        }
        self.actionFailed(err);
      });
  };

  /**
   * Report a failed action without destroying the page around it.
   *
   * The customer keeps their dashboard, keeps their real data, and gets one
   * line telling them what happened and what to do. Nothing is claimed about
   * whether the change applied unless we actually know.
   */
  Portal.prototype.actionFailed = function (err) {
    var code = (err && err.code) || 'server';
    var message;

    if (code === 'in_progress') {
      message = 'That is already being processed.';
    } else if (code === 'already_applied') {
      // The server refused a duplicate. The change IS in place, so this is
      // never phrased as a failure — phrasing it as one is what would send a
      // customer round again and skip a second delivery.
      message = 'That is already done \u2014 refresh to see the latest.';
    } else if (code === 'offer_unavailable') {
      // Not a failure and not the customer's fault: the offer simply cannot be
      // performed yet. Saying so plainly is the whole reason the CTA is live
      // rather than disabled — a dead button teaches nobody anything.
      message = 'That offer is not available yet. Nothing has changed.';
    } else if (code === 'stale_view') {
      message = 'This page is out of date — refresh, then try again.';
    } else if (code === 'timeout') {
      // The one case where the outcome is genuinely unknown. Say exactly that.
      message = 'That is taking longer than expected — refresh to check whether it went through.';
    } else if (code === 'not_enabled') {
      message = 'That is not available yet.';
    } else if (code === 'network') {
      message = 'No connection. Check your signal and try again.';
    } else if (code === 'unverified') {
      // The write was accepted but a fresh read did not show the new date.
      // Nothing is claimed as done; the customer is told to check before
      // trying again, so a second attempt cannot double-apply.
      message = 'We could not confirm that change yet. Refresh to check your next delivery date before trying again.';
    } else {
      message = 'That did not go through. Nothing has changed — please try again.';
    }

    // Step 6/step 7 keep their failure ON the screen, next to the button
    // that failed, as well as in the toast — the customer is still there.
    if (this.state.screen === 'cancel-offer') this.state.retainError = message;
    else if (this.state.screen === 'cancel-savings') this.state.offerError = message;
    this.toast(message);
    this.render();
  };

  /**
   * The write applied but the refresh did not.
   *
   * Never reported as a failure: the change is real. The customer is told it
   * worked and asked to refresh, rather than being told nothing happened and
   * being tempted to do it a second time.
   */
  Portal.prototype.refreshRequired = function () {
    this.toast('Done — refresh to see the updated dates.');
    this.render();
  };

  /** Re-read what a mutation may have changed. Requires a session. */
  Portal.prototype.refreshAuthenticatedData = function () {
    var self = this;
    if (!this.hasSession()) {
      return Promise.reject(NS.PortalError('unauthenticated', 'Sign in to view your subscription.'));
    }
    return this.adapter.getLoyalty().then(function (loy) {
      self.state.loyalty = loy;
      return self.adapter.listSubscriptions();
    }).then(function (subs) {
      self.state.inactive = subs.inactive;
      return self.adapter.listDeliveries();
    }).then(function (dels) {
      self.state.deliveries = dels;
    });
  };

  Portal.prototype.applyPending = function (on) {
    var host = this.root.querySelector('[data-spp-sheet-host]');
    var note = this.root.querySelector('[data-spp-pending-note]');
    if (host && this.state.sheet) host.classList.toggle('is-pending', !!on);
    if (note && this.state.sheet) note.hidden = !on;

    var buttons = this.root.querySelectorAll('[data-spp-act]');
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      var isThis = on && b.getAttribute('data-spp-act') === this.state.pending;
      b.classList.toggle('is-pending', !!isThis);
      // aria-disabled is what the delegated handler checks; `disabled` is what
      // stops the browser dispatching a click at all — including to a listener
      // this code did not attach, and including keyboard activation. Both, so
      // the guarantee does not depend on our own handler running first.
      if (on) {
        b.setAttribute('aria-disabled', 'true');
        if ('disabled' in b) b.disabled = true;
      } else {
        b.removeAttribute('aria-disabled');
        if ('disabled' in b) b.disabled = false;
      }
      var spinner = b.querySelector('.spp__spinner');
      if (isThis && !spinner) {
        var s = document.createElement('span');
        s.className = 'spp__spinner';
        b.insertBefore(s, b.firstChild);
      } else if (!isThis && spinner && spinner.parentNode === b) {
        b.removeChild(spinner);
      }
    }
  };

  /* =================================================================
   * View model
   * ================================================================= */

  Portal.prototype.viewModel = function () {
    var s = this.state, sub = s.data, loy = s.loyalty, cus = s.customer;
    var d = s.draft, self = this;
    var vm = {};

    if (cus) {
      // Live Phoenix data carries no display name, and the backend never
      // returns the address to the browser. Missing parts render as empty
      // rather than as the string "null".
      vm['customer.initials'] = cus.initials || '';
      vm['customer.firstName'] = cus.firstName || '';
      vm['customer.fullName'] = [cus.firstName, cus.lastName].filter(Boolean).join(' ');
      vm['customer.email'] = cus.email || '';
    }

    // One slot for the whole greeting. Splitting it into "Hi " plus a name slot
    // rendered a bare "Hi" whenever Phoenix had no first name, and a dangling
    // comma if punctuation had been added to the markup instead.
    vm['customer.greeting'] = cus && cus.firstName ? 'Hi, ' + cus.firstName : 'Hi there';
    // Same one-slot rule as the greeting above — no dangling comma when
    // Phoenix has no first name yet.
    vm['customer.welcomeBack'] = cus && cus.firstName ? 'Welcome back, ' + cus.firstName : 'Welcome back';

    if (sub) {
      vm['subscription.reference'] = sub.reference;
      vm['subscription.statusLabel'] = sub.status === 'active' ? 'Active' : 'Cancelled';
      vm['subscription.intervalDays'] = sub.intervalDays == null ? '' : String(sub.intervalDays);
      vm['subscription.nextOrderMedium'] = this.fmtDate(sub.nextOrderDate, 'medium');
      vm['subscription.nextOrderShort'] = this.fmtDate(sub.nextOrderDate, 'short');
      vm['subscription.nextOrderLong'] = this.fmtDate(sub.nextOrderDate, 'full');
      vm['subscription.startedLong'] = this.fmtDate(sub.startedOn, 'full');
      vm['subscription.deliveriesSoFar'] = sub.deliveriesSoFar == null ? '' : String(sub.deliveriesSoFar);
      vm['subscription.discountPercent'] = sub.discountRate == null ? '' : String(Math.round(sub.discountRate * 100));
      vm['subscription.daysAway'] = sub.daysUntilNextOrder + ' days away';
      vm['subscription.progressLabel'] = 'Next delivery in ' + sub.daysUntilNextOrder + ' days';
      vm['subscription.shipProgress'] = Math.max(6, 100 - Math.min(100, sub.daysUntilNextOrder * 1.6));
      // The backend returns city/province/country only, and withholds the
      // street line and postal code deliberately. Both the address and the
      // card can also be absent entirely, so every part is optional here.
      var addr = sub.address;
      if (addr) {
        vm['subscription.addressShort'] = [addr.city, addr.province].filter(Boolean).join(', ');
        vm['subscription.addressLines'] = [
          addr.name,
          addr.line1 ? addr.line1 + (addr.line2 ? ', ' + addr.line2 : '') : '',
          [addr.city, addr.province].filter(Boolean).join(', ') + (addr.zip ? ' ' + addr.zip : ''),
          addr.country
        ].filter(Boolean).map(function (x) { return self.escape(x); }).join('<br>');
      } else {
        vm['subscription.addressShort'] = '';
        vm['subscription.addressLines'] = '';
      }

      var pay = sub.payment;
      vm['subscription.paymentShort'] = pay && pay.brand
        ? pay.brand + (pay.last4 ? ' ···· ' + pay.last4 : '')
        : '';
      vm['subscription.paymentBrand'] = pay && pay.brand ? pay.brand.toUpperCase() : '';
      vm['subscription.paymentLast4'] = (pay && pay.last4) || '';
      vm['subscription.paymentExpiry'] = (pay && pay.expiry) || '';
      vm['subscription.quantitySummary'] = sub.lines.map(function (l) {
        return l.title.replace(/ (jar|pack).*$/, '') + ' ×' + l.quantity;
      }).join(', ');

      // Cancellation personalization — automatic, never a customer choice.
      // A single-product subscription (the vast majority) is named
      // directly on the started/reason/offer screens; a multi-product one
      // falls back to generic "Routine Care" wording, because there is no
      // honest way to pick ONE of several real products to speak for the
      // whole subscription. cancel(id) itself is unaffected either way —
      // cancelFacts below states plainly that EVERY product stops.
      var soleLine = sub.lines.length === 1 ? sub.lines[0] : null;
      var soleLineName = soleLine ? soleLine.title.replace(/ (jar|pack).*$/, '') : '';
      vm['cancel.focusName'] = soleLine ? soleLineName : 'Routine Care';
      vm['cancel.reasonHeading'] = soleLine ? ('Why are you cancelling ' + soleLineName + '?') : 'Why are you cancelling?';
      vm['cancel.startedHeading'] = soleLine ? ('Cancelling · ' + soleLine.title) : 'Cancelling · your Routine Care subscription';

      vm['pricing.total'] = this.fmtMoney(sub.pricing.total);
      vm['pricing.discount'] = this.fmtMoney(sub.pricing.discount);

      vm['sheet.skipToLong'] = this.fmtDate(NS.dates.addDays(sub.nextOrderDate, sub.intervalDays), 'long');
      var target = this.delayTargetIso();
      vm['sheet.delayToLong'] = target ? this.fmtDate(target, 'long') : 'Not chosen yet';
      // The confirmation the customer reads before committing: where the
      // delivery is now, and where it would go.
      vm['sheet.rescheduleFrom'] = this.fmtDate(sub.nextOrderDate, 'medium');
      vm['sheet.rescheduleTo'] = target ? this.fmtDate(target, 'medium') : '—';
      vm['sheet.rescheduleError'] = this.rescheduleError() || '';
    }

    // A failed VetPoints read is never rendered as a balance: loy.error marks
    // the ledger call that failed, and the screen shows its own error state
    // instead of these fields (see the data-spp-when="loyalty:*" toggle in
    // render(), below).
    if (loy && !loy.error) {
      vm['loyalty.points'] = String(loy.points);
      vm['loyalty.perRenewal'] = String(loy.perRenewal);
      vm['loyalty.nextRewardAt'] = String(loy.nextRewardAt);
      vm['loyalty.nextRewardName'] = loy.nextRewardName;
      vm['loyalty.toNextReward'] = String(loy.toNextReward);
      vm['loyalty.progressPercent'] = loy.progressPercent;
      vm['loyalty.disclosure'] = loy.disclosure || '';

      // The journey card's own header — matches the approved design exactly
      // (Claude Design "VetPets Portal V2", journeyNextName/journeyNextMeta/
      // journeyCounter). Computed straight from the real balance against the
      // same fixed ladder the milestone list itself uses — see listData()'s
      // 'milestones' case, just below.
      var ladder = NS.vetpointsMilestones;
      var jNext = null;
      for (var ji = 0; ji < ladder.length; ji++) {
        if (ladder[ji].points > loy.points) { jNext = ladder[ji]; break; }
      }
      var fmtPts = function (n) { return n.toLocaleString('en-US'); };
      vm['loyalty.journeyNextName'] = jNext ? jNext.name : 'Every reward unlocked';
      vm['loyalty.journeyNextMeta'] = jNext
        ? 'Unlocks at ' + fmtPts(jNext.points) + ' VetPoints · ' + fmtPts(Math.max(0, jNext.points - loy.points)) + ' points to go'
        : 'You have reached every milestone on the journey.';
      vm['loyalty.journeyCounter'] = jNext
        ? fmtPts(loy.points) + ' / ' + fmtPts(jNext.points)
        : fmtPts(loy.points) + ' / ' + fmtPts(ladder[ladder.length - 1].points);
    }
    vm['label.retryLoyalty'] = s.loyaltyPending ? 'Retrying…' : 'Try again';

    vm['account.activeCount'] = sub && sub.status !== 'cancelled' ? '1 active' : '0 active';
    vm['account.inactiveCount'] = String(s.inactive.length);

    // Retention offers — skip or delay only. Nothing else is supported.
    var shortNext = sub ? this.fmtDate(sub.nextOrderDate, 'short') : '';
    var alts = {
      price: ['Would a longer gap help?',
        'Skipping the next delivery lowers what you spend this month without changing what arrives.',
        'Skip ' + shortNext, 'You won\'t be charged for that delivery, and the one after it stays on schedule.',
        'Skip this delivery', 'skip'],
      stock: ['Then skip the next one',
        'Nothing has to ship until you need it. Skipping keeps your price and your place in the routine.',
        'Skip ' + shortNext, 'You won\'t be charged for that delivery. The one after it stays on schedule.',
        'Skip this delivery', 'skip'],
      // Identity-free. These strings are written into [data-spp-field] slots
      // by render(), so clearPlaceholders() never sees them — naming the
      // prototype's pets here would put them in front of a real customer.
      pet: ['Give it a little longer?',
        'If you are already stocked up, skipping costs nothing and keeps everything else in place.',
        'Skip ' + shortNext, 'Your price and your place in the routine are kept.',
        'Skip this delivery', 'skip'],
      'switch': ['Take a break first?',
        'Skipping the next delivery gives you time to decide, and nothing is charged in the meantime.',
        'Skip ' + shortNext, 'Your price and your place in the routine are kept.',
        'Skip this delivery', 'skip'],
      other: ['Before you cancel',
        'Two quicker options that keep your subscriber price. Cancelling stays available below.',
        'Skip ' + shortNext, 'Push the next delivery back without any charge.',
        'Skip this delivery', 'skip']
    };
    var alt = alts[d.reason] || alts.other;
    vm['alt.headline'] = alt[0];
    vm['alt.body'] = alt[1];
    vm['alt.primaryTitle'] = alt[2];
    vm['alt.primaryBody'] = alt[3];
    vm['alt.primaryCta'] = alt[4];
    this._altAction = alt[5];

    vm['error.reference'] = s.error ? s.error.reference : '';
    if (s.success) {
      vm['success.title'] = s.success.title;
      vm['success.body'] = s.success.body;
    }

    // Cancellation Retention V2.
    var chosenReason = null;
    for (var ri = 0; ri < REASONS.length; ri++) {
      if (REASONS[ri][0] === d.reason) chosenReason = REASONS[ri];
    }
    vm['reason.label'] = chosenReason ? chosenReason[1] : 'Not given';
    vm['offer.percent'] = String(OFFER_PERCENT);
    vm['offer.standard'] = String(STANDARD_PERCENT);

    // What the next delivery costs now, and what it would cost with the offer.
    // Computed from the upcoming charge the dashboard already displays, so the
    // screen can never quote a figure the server would not send.
    var upcoming = sub && sub.pricing && sub.pricing.total ? sub.pricing.total : null;
    if (upcoming && typeof upcoming.amount === 'number' && upcoming.amount > 0) {
      var offerAmount = Math.max(1, Math.round(upcoming.amount * (1 - OFFER_PERCENT / 100) * 100)) / 100;
      vm['offer.currentPrice'] = this.fmtMoney(upcoming);
      vm['offer.price'] = this.fmtMoney({ amount: offerAmount, currencyCode: upcoming.currencyCode });
    } else {
      vm['offer.currentPrice'] = '';
      vm['offer.price'] = '';
    }

    var p = s.pending;
    vm['label.sendLink'] = p === 'sendLink' ? 'Sending link…' : 'Email me a sign-in link';
    vm['label.resend'] = p === 'resend' ? 'Sending…' : 'Resend link';
    vm['label.skip'] = p === 'skip' ? 'Skipping delivery…' : 'Skip this delivery';
    var delayTarget = sub ? this.delayTargetIso() : null;
    vm['label.delay'] = p === 'delay'
      ? 'Rescheduling…'
      : (delayTarget ? 'Move to ' + this.fmtDate(delayTarget, 'short') : 'Choose a date');
    vm['label.cancel'] = p === 'cancel' ? 'Cancelling…' : 'Yes, cancel my subscription';
    vm['label.applyGap'] = p === 'applyGap' ? 'Updating…' : 'Confirm this change';
    vm['label.acceptOffer'] = p === 'acceptOffer'
      ? 'Applying…'
      : 'Apply ' + OFFER_PERCENT + '% to my next delivery';
    vm['label.reactivate'] = p === 'reactivate' ? 'Reactivating…' : 'Reactivate subscription';

    return vm;
  };

  Portal.prototype.escape = function (str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  };

  /* =================================================================
   * Render
   * ================================================================= */

  Portal.prototype.render = function () {
    var vm = this.viewModel();
    var i;

    var fields = this.root.querySelectorAll('[data-spp-field]');
    for (i = 0; i < fields.length; i++) {
      var key = fields[i].getAttribute('data-spp-field');
      if (key === 'toast.message') continue;
      if (Object.prototype.hasOwnProperty.call(vm, key)) fields[i].textContent = vm[key];
    }

    var htmlFields = this.root.querySelectorAll('[data-spp-field-html]');
    for (i = 0; i < htmlFields.length; i++) {
      var hk = htmlFields[i].getAttribute('data-spp-field-html');
      if (Object.prototype.hasOwnProperty.call(vm, hk)) htmlFields[i].innerHTML = vm[hk];
    }

    var ariaFields = this.root.querySelectorAll('[data-spp-field-aria]');
    for (i = 0; i < ariaFields.length; i++) {
      var ak = ariaFields[i].getAttribute('data-spp-field-aria');
      if (Object.prototype.hasOwnProperty.call(vm, ak)) ariaFields[i].setAttribute('aria-label', vm[ak]);
    }

    var widths = this.root.querySelectorAll('[data-spp-style-width]');
    for (i = 0; i < widths.length; i++) {
      var wk = widths[i].getAttribute('data-spp-style-width');
      if (Object.prototype.hasOwnProperty.call(vm, wk)) widths[i].style.width = vm[wk] + '%';
    }

    // Status is a single canonical value: active or cancelled. At most one
    // conditional block can ever be visible.
    var status = this.state.data ? this.state.data.status : 'active';

    // A failed VetPoints read (anything but an expired session, which signs
    // the whole visit out instead) is its own condition, checked here rather
    // than folded into `loy` truthiness so a screen can show "no data yet"
    // and "the read failed" differently if it ever needs to.
    var loy = this.state.loyalty;
    var loyaltyFailed = !!(loy && loy.error);
    var loyaltyHistoryCount = (loy && !loy.error && loy.history) ? loy.history.length : 0;
    var loyaltyAllReached = !!(loy && !loy.error && loy.allMilestonesReached);

    var conds = this.root.querySelectorAll('[data-spp-when]');
    for (i = 0; i < conds.length; i++) {
      var expr = conds[i].getAttribute('data-spp-when').split(':');
      if (expr[0] === 'status') conds[i].hidden = status !== expr[1];
      if (expr[0] === 'loyalty') conds[i].hidden = (expr[1] === 'error') !== loyaltyFailed;
      if (expr[0] === 'loyaltyHistory') conds[i].hidden = (expr[1] === 'has') !== (loyaltyHistoryCount > 0);
      if (expr[0] === 'loyaltyMilestones') conds[i].hidden = (expr[1] === 'complete') !== loyaltyAllReached;
    }

    var badge = this.root.querySelector('[data-spp-status-badge]');
    if (badge) badge.style.background = status === 'active' ? 'var(--spp-light)' : 'var(--spp-surface-neutral)';

    this.renderLists();
    this.renderDatePicker();
    this.applyLoyaltyVisibility();
    this.renderCancelJourney();
    this.renderRetain();
  };

  /**
   * The two conditional pieces of the cancellation journey.
   *
   * The free-text box appears only for "Something else", and the reason row
   * on the confirmation screen only once a reason exists — an empty "Reason on
   * record" line would be worse than none.
   */
  Portal.prototype.renderCancelJourney = function () {
    var d = this.state.draft;

    var note = this.root.querySelector('[data-spp-reason-note]');
    if (note) note.hidden = d.reason !== 'other';

    var gapDate = this.root.querySelector('[data-spp-gap-date]');
    if (gapDate) gapDate.hidden = d.gap !== 'date';

    var row = this.root.querySelector('[data-spp-reason-row]');
    if (row) row.hidden = !d.reason;

    // Step 7's own failure slot — see actionFailed()'s offerError branch.
    var offerErr = this.root.querySelector('[data-spp-offer-error]');
    if (offerErr) {
      offerErr.textContent = this.state.offerError || '';
      offerErr.hidden = !this.state.offerError;
    }

    // The message disappears as soon as the problem does, rather than sitting
    // there contradicting what the customer has just put right. A save
    // failure is a DIFFERENT kind of message — the selection is already
    // valid, delivering it is what failed — so it is shown regardless of
    // reasonProblem() and takes priority in the same slot.
    var err = this.root.querySelector('[data-spp-reason-error]');
    if (err) {
      var msg = this.state.reasonSaveError
        ? this.state.reasonSaveError
        : (this.state.reasonError && this.reasonProblem() ? this.state.reasonError : '');
      err.textContent = msg;
      err.hidden = !msg;
    }

    /* THE BUG THIS EXISTS FOR:
     *
     * The confirmation screen's Back button was wired to a fixed
     * data-spp-go="cancel-savings" in the markup. For a customer who has
     * already redeemed the 40% offer, show('cancel-savings') immediately
     * redirects back to 'cancel-confirm' — the SAME screen the customer is
     * already on — so the button appeared to do nothing at all. It was
     * never dead; it was navigating to a screen that refuses to be shown,
     * which bounced the customer right back where they started.
     *
     * The target is decided fresh on every render because eligibility is
     * server truth that can change mid-session — most obviously the moment
     * a successful offer's post-write load() lands and flips
     * retentionOfferRedeemed from false to true. Applies to every reason
     * now, not just whichever one used to earn the offer. */
    var confirmBack = this.root.querySelector('[data-spp-confirm-back]');
    if (confirmBack) {
      var redeemed = !!(this.state.data && this.state.data.retentionOfferRedeemed);
      // Adjust your routine (not the reason screen) is the screen
      // immediately before the offer in the new order — that is the safe
      // fallback once the offer screen itself refuses to show.
      confirmBack.setAttribute('data-spp-go', redeemed ? 'cancel-offer' : 'cancel-savings');
    }
  };

  /**
   * The custom-date field: shown only when the customer asked for one, and
   * bounded to exactly the window the server accepts.
   *
   * The input is never re-set while it holds focus. Writing `value` on every
   * render would fight the customer mid-edit and, in some browsers, reset the
   * caret on each keystroke.
   */
  Portal.prototype.renderDatePicker = function () {
    var wrap = this.root.querySelector('[data-spp-custom-date]');
    if (!wrap) return;

    var on = this.isCustomDate();
    wrap.hidden = !on;
    if (!on) return;

    var input = wrap.querySelector('[data-spp-date]');
    if (!input) return;

    var b = this.rescheduleBounds();
    input.min = b.min;
    input.max = b.max;
    if (document.activeElement !== input) input.value = this.state.draft.date || '';

    var err = this.rescheduleError();
    // Only complain once the customer has actually chosen something. An empty
    // field on opening is not a mistake they have made yet.
    var showErr = !!err && !!this.state.draft.date;
    input.setAttribute('aria-invalid', showErr ? 'true' : 'false');

    var msg = wrap.querySelector('[data-spp-date-error]');
    if (msg) {
      msg.textContent = showErr ? err : '';
      msg.hidden = !showErr;
    }
  };

  /* -----------------------------------------------------------------
   * Lists
   * ----------------------------------------------------------------- */

  var PENDING_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<rect x="3.2" y="5.2" width="17.6" height="13.6" rx="2.6" stroke="currentColor" stroke-width="1.6"/>' +
    '<circle cx="8.6" cy="10" r="1.6" fill="currentColor"/>' +
    '<path d="M3.6 16.4l4.6-4 3.4 3 3.2-2.6 5.4 4.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /* VetPoints milestone ladder badges — same paths as spp-icon.liquid's
     'check-circle' and 'lock', inlined because a list row is rebuilt in JS,
     not rendered through Liquid. Keep both in sync with spp-icon.liquid by
     hand if that snippet's paths ever change. */
  /* The node itself (.spp__ms-node) supplies the achieved/current/locked
     background and border per the approved design — these three are the
     icon ALONE, exactly the paths from that design, so they drop into
     whichever node background is already showing. */
  var MILESTONE_CHECK_SVG = '<svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M3.9 9.5l3.2 3.2 7-7.4" stroke="#FFFFFF" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  var MILESTONE_CURRENT_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<rect x="3.6" y="10.4" width="16.8" height="10.2" rx="2" stroke="var(--spp-accent)" stroke-width="1.9"/>' +
    '<rect x="2.4" y="6.6" width="19.2" height="4.2" rx="1.5" stroke="var(--spp-accent)" stroke-width="1.9"/>' +
    '<path d="M12 6.6v14" stroke="var(--spp-accent)" stroke-width="1.9"/>' +
    '<path d="M12 6.6H8.9c-1.3 0-2.3-1-2.3-2.1 0-1.1.9-1.9 2-1.9 2.1 0 3.4 4 3.4 4zM12 6.6h3.1c1.3 0 2.3-1 2.3-2.1 0-1.1-.9-1.9-2-1.9-2.1 0-3.4 4-3.4 4z" stroke="var(--spp-accent)" stroke-width="1.9" stroke-linejoin="round"/>' +
    '</svg>';
  var MILESTONE_LOCK_SVG = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
    '<rect x="3.4" y="7" width="9.2" height="6.6" rx="1.6" stroke="#A9BDCC" stroke-width="1.6"/>' +
    '<path d="M5.6 7V5.4a2.4 2.4 0 014.8 0V7" stroke="#A9BDCC" stroke-width="1.6" stroke-linecap="round"/>' +
    '</svg>';

  /**
   * The store's product images, keyed by Shopify product and variant id.
   *
   * Emitted by the section from Liquid, because Liquid is the only part of
   * this system that has the catalogue. Parsed once; a malformed or absent
   * block simply means every line falls back to the branded pending tile.
   */
  Portal.prototype.imageMap = function () {
    if (this._imageMap) return this._imageMap;
    this._imageMap = {};
    try {
      var el = this.root.querySelector('[data-spp-image-map]');
      if (el && el.textContent) {
        var parsed = JSON.parse(el.textContent);
        for (var key in parsed) {
          if (!Object.prototype.hasOwnProperty.call(parsed, key)) continue;
          var entry = parsed[key];
          if (entry && entry.src) this._imageMap[String(key)] = entry;
        }
      }
    } catch (e) {
      // A bad map is not a reason to fail a render.
    }
    return this._imageMap;
  };

  /**
   * Resolve a line's product image, or nothing.
   *
   * Never guesses: an id that is not in the map returns null and the caller
   * renders the branded packshot-pending tile. Showing the wrong product's
   * photo would be worse than showing none.
   */
  Portal.prototype.imageForLine = function (line) {
    if (!line) return null;
    var map = this.imageMap();
    var keys = [line.variantId, line.productId, line.id];
    for (var i = 0; i < keys.length; i++) {
      if (keys[i] == null) continue;
      var hit = map[String(keys[i])];
      if (hit && hit.src) return hit;
    }
    return null;
  };

  /** Fill _image/_pending/_alt2 for one line, mock or live. */
  Portal.prototype.lineImage = function (line) {
    // Mock mode carries its own images on the line itself.
    if (line.image) return { _image: line.image, _pending: false, _alt2: line.title || '' };
    var resolved = this.imageForLine(line);
    if (resolved) {
      return { _image: resolved.src, _pending: false, _alt2: resolved.alt || line.title || '' };
    }
    return { _image: '', _pending: true, _alt2: line.title || '' };
  };

  Portal.prototype.listData = function (name) {
    var s = this.state, sub = s.data, d = s.draft, self = this;

    switch (name) {
      // Pets are a mock-only concept: Phoenix exposes no pet record, so the
      // live adapter returns a customer without them.
      case 'pets':
        return ((s.customer && s.customer.pets) || []).map(function (p, i) {
          return { name: p.name, initial: p.initial, _alt: i > 0 };
        });

      case 'petsFull':
        return ((s.customer && s.customer.pets) || []).map(function (p, i) {
          return { initial: p.initial, nameBreed: p.name + ' · ' + p.breed, _alt: i > 0 };
        });

      case 'lineThumbs':
      case 'lineThumbs2':
        return (sub ? sub.lines : []).map(function (l) {
          return self.lineImage(l);
        });

      case 'lines':
        return (sub ? sub.lines : []).map(function (l) {
          var img = self.lineImage(l);
          return {
            title: l.title, subtitle: l.subtitle, quantity: String(l.quantity),
            unitPrice: self.fmtMoney(l.unitPrice),
            _image: img._image, _pending: img._pending, _alt2: img._alt2
          };
        });

      case 'lineSummary':
        return (sub ? sub.lines : []).map(function (l) {
          return { title: l.title, quantity: String(l.quantity), linePrice: self.fmtMoney(l.linePrice) };
        });

      case 'deliveriesPreview': {
        if (!s.deliveries) return [];
        var out = [];
        var up = s.deliveries.upcoming;
        // There is no upcoming delivery for a cancelled subscription, and no
        // date to format if Phoenix has not scheduled one.
        if (up && up.date) out.push({
          mon: NS.dates.parseISO(up.date).toLocaleDateString('en', { month: 'short' }).toUpperCase(),
          day: String(NS.dates.parseISO(up.date).getDate()),
          title: up.title, meta: up.items, status: up.status
        });
        (s.deliveries.past || []).slice(0, 2).forEach(function (o) {
          if (!o.date) return;
          out.push({
            mon: NS.dates.parseISO(o.date).toLocaleDateString('en', { month: 'short' }).toUpperCase(),
            day: String(NS.dates.parseISO(o.date).getDate()),
            title: 'Delivered',
            meta: [o.orderId ? 'Order #' + o.orderId : '', self.fmtMoney(o.amount)]
              .filter(Boolean).join(' · '),
            status: 'Complete'
          });
        });
        return out;
      }

      case 'pastOrders':
        return ((s.deliveries && s.deliveries.past) || []).map(function (o) {
          return {
            dateLong: self.fmtDate(o.date, 'full'), items: o.items,
            amount: self.fmtMoney(o.amount), status: o.status,
            _image: self.cfg.images.freshwipes, _pending: false, _alt2: ''
          };
        });

      case 'pointsHistory':
        return (s.loyalty && !s.loyalty.error && s.loyalty.history ? s.loyalty.history : []).map(function (h) {
          return {
            label: h.label,
            dateLong: self.fmtDate(h.date, 'full'),
            delta: (h.delta > 0 ? '+' : '−') + Math.abs(h.delta)
          };
        });

      case 'milestones': {
        // The real balance is the only thing that decides achieved/current/
        // locked. The ladder itself (thresholds, names, images) is fixed —
        // see VetPetsPortal.vetpointsMilestones — and identical to what the
        // backend awards against, so this never needs its own data source.
        //
        // The fillA/fillB math below is copied verbatim from the approved
        // Claude Design mockup ("VetPets Portal V2", the `journey` builder)
        // so the connecting line between two nodes fills continuously and
        // meets in the middle exactly as designed, not as a binary on/off.
        var balance = (s.loyalty && !s.loyalty.error) ? s.loyalty.points : 0;
        var ladder = NS.vetpointsMilestones;
        var nextIdx = -1;
        for (var mi = 0; mi < ladder.length; mi++) {
          if (ladder[mi].points > balance) { nextIdx = mi; break; }
        }
        function clamp01(n) { return Math.max(0, Math.min(1, n)); }
        function fmtN(n) { return n.toLocaleString('en-US'); }
        return ladder.map(function (m, i) {
          var done = balance >= m.points;
          var isNext = i === nextIdx;
          var live = done || isNext;
          var segNext = i < ladder.length - 1
            ? clamp01((balance - m.points) / (ladder[i + 1].points - m.points))
            : 0;
          var segPrev = i > 0
            ? clamp01((balance - ladder[i - 1].points) / (m.points - ladder[i - 1].points))
            : 0;
          return {
            name: m.name,
            ptsNumber: fmtN(m.points),
            sub: done ? 'Unlocked' : (isNext ? (fmtN(Math.max(0, m.points - balance)) + ' points to go') : 'Locked'),
            _image: m.image || '',
            _pending: !m.image,
            _alt2: m.name,
            _milestoneState: done ? 'achieved' : (isNext ? 'current' : 'locked'),
            _isFirst: i === 0,
            _isLast: i === ladder.length - 1,
            _fillA: Math.round(clamp01(segPrev * 2 - 1) * 100),
            _fillB: Math.round(clamp01(segNext * 2) * 100)
          };
        });
      }

      case 'rewards':
        return s.rewards.map(function (r) {
          var label = r.pendingRequest ? 'Requested'
            : r.affordable ? 'Redeem' : (r.pointsToGo + ' points to go');
          return {
            name: r.name, cost: String(r.cost), btnLabel: label,
            _image: r.image, _pending: r.imagePending, _alt2: r.name,
            _rewardId: r.id,
            _affordable: r.affordable && !r.pendingRequest
          };
        });

      case 'reasons':
        return REASONS.map(function (r) {
          return { label: r[1], _value: r[0], _checked: d.reason === r[0] };
        });


      case 'gapOptions':
        return GAP_OPTIONS.map(function (g) {
          return { label: g[1], meta: g[2], _value: g[0], _checked: d.gap === g[0] };
        });

      case 'delayOptions': {
        var tiles = [7, 15, 30].map(function (n) {
          return { label: n + ' days', _value: n, _checked: d.delay === n };
        });
        // A fourth tile, styled exactly like the presets, so choosing a
        // specific date is as ordinary as choosing "7 days".
        tiles.push({ label: 'Choose another date', _value: 'custom', _checked: d.delay === 'custom' });
        return tiles;
      }

      case 'restartDates':
        return [['As soon as possible', 0], ['In two weeks', 14], ['In a month', 30]]
          .map(function (r, i) { return { label: r[0], _value: r[1], _checked: d.restart === i, _index: i }; });

      case 'inactiveSubs':
        return s.inactive.map(function (x) {
          // No `reference`: nothing renders it any more, and supplying an
          // internal id to a template is how it finds its way back on screen.
          return {
            statusLabel: x.status === 'cancelled' ? 'Cancelled' : 'Completed',
            name: x.name, meta: x.meta,
            _image: x.image, _pending: false, _alt2: x.name
          };
        });

      case 'cancelFacts': {
        if (!sub) return [];
        var card = sub.payment;
        var facts = [];
        var factLines = sub.lines || [];
        // Only the product the customer named on step 1 was ever discussed
        // on the screens since — this is the one place that must say, in
        // plain words, that confirming ends every product in the
        // subscription, not only that one. Skipped when there is only one
        // product: there is nothing to disambiguate.
        if (factLines.length > 1) {
          facts.push(
            'This cancels your whole Routine Care subscription — ' +
            factLines.map(function (l) { return l.title.replace(/ (jar|pack).*$/, ''); }).join(', ') +
            ' will all stop, not only the product you told us about.'
          );
        }
        facts.push('Your ' + self.fmtDate(sub.nextOrderDate, 'short') + ' delivery will not ship.');
        facts.push(card && card.brand
          ? 'No further charges will be made to ' + card.brand +
            (card.last4 ? ' ···· ' + card.last4 : '') + '.'
          : 'No further charges will be made.');
        facts.push('You can reactivate with the same products and price at any time.');
        // Last, and phrased as care rather than pressure: by this screen the
        // decision is made, and a sales pitch here would read as one.
        facts.push('RoutineCare keeps daily care consistent, helping prevent buildup before it becomes a recurring problem.');
        return facts.map(function (t) { return { text: t }; });
      }

      case 'cancelReviews': {
        // One real review per distinct real product category in this
        // subscription, in a fixed priority order, falling back to a
        // second real review for the SAME category so there are always
        // two cards — never a fabricated one for a product not on this
        // account. See REVIEW_BANK above.
        //
        // Keyed off l.title, not a product id: neither adapter mode
        // (see projectSubscription(), mock and live) puts an internal
        // product key on the line objects it hands to the portal — title
        // is the only field guaranteed to survive in both, and every real
        // product title contains its own name ("FreshWipes jar",
        // "EyeWipes jar", a live Phoenix title, etc.).
        if (!sub) return [];
        var seenKeys = [];
        (sub.lines || []).forEach(function (l) {
          var t = (l.title || '').toLowerCase();
          var key = t.indexOf('fresh') !== -1 ? 'freshwipes'
            : t.indexOf('eye') !== -1 ? 'eyewipes'
            : null;
          if (key && seenKeys.indexOf(key) === -1) seenKeys.push(key);
        });
        if (seenKeys.length === 0) seenKeys.push('freshwipes');
        var picks = [];
        if (seenKeys.length >= 2) {
          picks.push({ key: seenKeys[0], idx: 0 });
          picks.push({ key: seenKeys[1], idx: 0 });
        } else {
          picks.push({ key: seenKeys[0], idx: 0 });
          picks.push({ key: seenKeys[0], idx: 1 });
        }
        return picks.map(function (p) {
          var bank = REVIEW_BANK[p.key];
          var r = bank.quotes[p.idx] || bank.quotes[0];
          return { quote: r.quote, name: r.name, category: bank.category };
        });
      }

      case 'retainPoints':
        return this.retainContent().points.map(function (t) { return { text: t }; });

      case 'retainTips':
        return this.retainContent().tips.map(function (t) { return { text: t }; });

      case 'retainTimeline':
        return this.retainContent().timeline;

      case 'retainAdjRows': {
        var adjNext = sub && sub.nextOrderDate;
        var adjMeta = {
          skip: adjNext ? 'Nothing ships ' + self.fmtDate(adjNext, 'short') + '. The one after is unchanged.' : 'Nothing ships. The one after is unchanged.',
          move: '7, 15 or 30 days later \u2014 your cadence stays the same',
          freq: sub && sub.intervalDays ? 'Currently every ' + sub.intervalDays + ' days' : 'Move your next delivery later',
          pause: 'Choose the date your deliveries resume'
        };
        return RETAIN_ADJUSTMENTS.map(function (a) {
          return { label: a[1], meta: adjMeta[a[0]], _value: a[0], _checked: d.retainAdj === a[0] };
        });
      }

      case 'retainAdjTiles':
        if (d.retainAdj === 'pause') {
          return [['m1', '1 month'], ['m2', '2 months'], ['m3', '3 months']].map(function (t) {
            return { label: t[1], _value: t[0], _checked: d.retainTile === t[0] };
          });
        }
        return [7, 15, 30].map(function (n) {
          return { label: n + ' days', _value: 'd' + n, _checked: d.retainTile === 'd' + n };
        });

      case 'startedCategories':
        // Cancel step 3 ("why you started"). A soft, non-blocking question
        // — the pick only drives this row's own selected styling; it is
        // never sent to Phoenix and never personalizes any other screen
        // (unlike the product name, which comes from real subscription
        // data — see cancel.startedHeading in viewModel()).
        return STARTED_CATEGORIES.map(function (c) {
          return { name: c[1], subtitle: c[2], _value: c[0], _checked: d.startedCategory === c[0] };
        });

      default:
        return [];
    }
  };

  Portal.prototype.renderLists = function () {
    var hosts = this.root.querySelectorAll('[data-spp-list]');
    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      var name = host.getAttribute('data-spp-list');
      var tpl = host.querySelector('[data-spp-tpl="' + name + '"]');
      if (!tpl) continue;

      var items = this.listData(name);
      var withSeparators = host.hasAttribute('data-spp-separators');

      /* The reason list's 8 rows never change in count or shape once
       * mounted. The destroy-and-rebuild path below removes every row
       * before appending fresh ones, which briefly collapses the page's
       * height below the viewport — long enough for the browser to clamp
       * scroll back to 0, a jump the customer never asked for that
       * strands whichever row they meant to click next out from under a
       * pointer that hasn't moved. Updating the existing rows in place
       * keeps all 8 mounted (and focus where the customer left it) and
       * leaves scroll position alone. */
      if (name === 'reasons' && !withSeparators) {
        var kept = [];
        for (var c = 0; c < host.children.length; c++) {
          if (host.children[c] !== tpl) kept.push(host.children[c]);
        }
        if (kept.length === items.length) {
          for (var e = 0; e < items.length; e++) this.fillNode(kept[e], items[e], name, e);
          continue;
        }
      }

      var kids = Array.prototype.slice.call(host.children);
      for (var k = 0; k < kids.length; k++) if (kids[k] !== tpl) host.removeChild(kids[k]);

      for (var i = 0; i < items.length; i++) {
        if (withSeparators && i > 0) {
          var hr = document.createElement('div');
          hr.style.cssText = 'height:1px;background:rgba(13,35,64,.09);';
          host.appendChild(hr);
        }
        var node = tpl.content.firstElementChild.cloneNode(true);
        this.fillNode(node, items[i], name, i);
        host.appendChild(node);
      }
    }
  };

  Portal.prototype.fillNode = function (node, item, listName, index) {
    var i;

    var fields = node.querySelectorAll('[data-spp-field]');
    for (i = 0; i < fields.length; i++) {
      var key = fields[i].getAttribute('data-spp-field');
      if (Object.prototype.hasOwnProperty.call(item, key)) fields[i].textContent = item[key];
    }
    if (node.hasAttribute && node.hasAttribute('data-spp-field')) {
      var rk = node.getAttribute('data-spp-field');
      if (Object.prototype.hasOwnProperty.call(item, rk)) node.textContent = item[rk];
    }

    var thumb = node.matches && node.matches('[data-spp-thumb]') ? node : node.querySelector('[data-spp-thumb]');
    if (thumb) {
      var img = thumb.querySelector('[data-spp-img]');
      if (item._pending) {
        thumb.classList.add('spp__thumb--pending');
        thumb.innerHTML = PENDING_SVG;
        thumb.setAttribute('role', 'img');
        thumb.setAttribute('aria-label', (item._alt2 || 'Product') + ' — packshot photo pending');
      } else if (img) {
        if (item._image) {
          img.src = item._image;
          // Named, not decorative: in the thumb strip the photo is the only
          // thing identifying the product.
          img.alt = item._alt2 || '';
          img.loading = 'lazy';
          img.decoding = 'async';
          // Reserve the box so the row does not jump when the photo arrives.
          // The CSS sizes the tile; these stop the intrinsic ratio from
          // being unknown until load.
          img.setAttribute('width', '64');
          img.setAttribute('height', '64');
        } else {
          img.remove();
        }
      }
    }

    if (typeof item._checked === 'boolean') {
      var pick = node.matches && node.matches('[data-spp-pick]') ? node : node.querySelector('[data-spp-pick]');
      if (pick) {
        pick.setAttribute('aria-checked', item._checked ? 'true' : 'false');
        pick.classList.toggle('is-selected', item._checked);
      }
    }

    var carriers = node.querySelectorAll('[data-spp-pick], [data-spp-act]');
    var all = Array.prototype.slice.call(carriers);
    if (node.matches && (node.matches('[data-spp-pick]') || node.matches('[data-spp-act]'))) all.push(node);
    for (i = 0; i < all.length; i++) {
      if (item._value !== undefined) all[i].dataset.sppValue = item._value;
      if (item._rewardId) all[i].dataset.sppReward = item._rewardId;
      if (item._index !== undefined) all[i].dataset.sppIndex = item._index;
      all[i].dataset.sppListIndex = index;
    }

    if (item._alt) {
      var dot = node.querySelector('.spp__chip-dot');
      if (dot) dot.classList.add('spp__chip-dot--alt');
    }

    if (listName === 'rewards') {
      var btn = node.querySelector('[data-spp-act="requestRedemption"]');
      if (btn) {
        if (item._affordable) {
          btn.classList.add('spp__btn--primary');
          btn.style.background = '';
          btn.style.color = '';
        } else {
          btn.classList.remove('spp__btn--primary');
          btn.style.background = 'var(--spp-surface-neutral)';
          btn.style.color = 'var(--spp-muted)';
          btn.setAttribute('aria-disabled', 'true');
        }
      }
    }

    if (listName === 'milestones') {
      // node here is one .spp__ms-col (desktop) or .spp__ms-vrow (mobile) —
      // the outer element cloned from whichever of the two templates this
      // call is filling. Both carry the same state classes and the same
      // inner [data-spp-ms-*] parts, so everything below works for either.
      node.classList.remove('spp__ms-col--achieved', 'spp__ms-col--current', 'spp__ms-col--locked');
      node.classList.add('spp__ms-col--' + item._milestoneState);
      node.classList.toggle('spp__ms-col--edge-a', !!item._isFirst);
      node.classList.toggle('spp__ms-col--edge-b', !!item._isLast);

      var nodeIcon = node.querySelector('[data-spp-ms-node]');
      if (nodeIcon) {
        nodeIcon.innerHTML = item._milestoneState === 'achieved' ? MILESTONE_CHECK_SVG
          : item._milestoneState === 'current' ? MILESTONE_CURRENT_SVG
          : MILESTONE_LOCK_SVG;
      }

      var fillA = node.querySelector('[data-spp-ms-fill-a]');
      var fillB = node.querySelector('[data-spp-ms-fill-b]');
      // Desktop tracks run horizontally (width); the mobile vertical track
      // reuses the identical markup rotated with CSS, so the fill there has
      // to be a height instead. `node` is a detached clone at this point
      // (fillNode runs before the caller appends it — see renderLists), so
      // this reads the root element's OWN class rather than walking to an
      // ancestor that does not exist yet.
      var isVertical = node.classList.contains('spp__ms-vrow');
      var dim = isVertical ? 'height' : 'width';
      if (fillA) fillA.style[dim] = (item._fillA || 0) + '%';
      if (fillB) fillB.style[dim] = (item._fillB || 0) + '%';
    }
  };

  /* =================================================================
   * Events
   * ================================================================= */

  Portal.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var el;
      if ((el = e.target.closest('[data-spp-go]'))) {
        e.preventDefault();
        var goTarget = el.getAttribute('data-spp-go');
        /* Declining a recommendation has no dedicated action — it is just
         * navigating on without having accepted it. Checked BEFORE show()
         * changes the screen, so "leaving X toward Y" is exactly what
         * distinguishes a decline from every other way of reaching the
         * next screen (the Longer Gap path, or the already-redeemed
         * redirect show() itself performs).
         *
         * Two separate declines now: leaving cancel-offer ("Adjust your
         * routine") toward cancel-savings declines whatever reason-
         * personalized recommendation was shown there (never 'offer' any
         * more); leaving cancel-savings toward cancel-confirm declines the
         * 40% offer itself, which is the only place offer_declined can
         * still fire from. */
        if (self.state.screen === 'cancel-offer' && goTarget === 'cancel-savings' && self.adapter.recordRetentionEvent) {
          self.trackRetention('recommendation_declined', self.retainKind().action);
        } else if (self.state.screen === 'cancel-savings' && goTarget === 'cancel-confirm' && self.adapter.recordRetentionEvent) {
          if (self.state.retentionJourneyId) {
            self.adapter.recordRetentionEvent('offer_declined', self.state.retentionJourneyId, 'next_delivery_40').catch(function () {});
          }
          self.trackRetention('recommendation_declined', 'next_delivery_40');
        }
        self.show(goTarget);
        return;
      }
      if ((el = e.target.closest('[data-spp-sheet]'))) {
        e.preventDefault(); self.openSheet(el.getAttribute('data-spp-sheet')); return;
      }
      if ((el = e.target.closest('[data-spp-pick]'))) {
        e.preventDefault();
        /* TEMPORARY DIAGNOSTIC — DEV ONLY, NOT SHIPPED TO LIVE.
         * Added to capture a live-reported "some reasons don't select"
         * defect that a synthetic DOM harness could not reproduce. Logs only
         * the attempted reason code, the raw click target's tag/class, the
         * resolved [data-spp-pick] element's tag/class, and draft.reason
         * before/after pick() runs. No PII, no email, no Phoenix id.
         * Remove once the live defect is captured and understood. */
        if (el.getAttribute('data-spp-pick') === 'reason') {
          var __diagValue = el.dataset.sppValue;
          var __diagTargetTag = e.target.tagName;
          var __diagTargetClass = e.target.className;
          var __diagBefore = self.state.draft.reason;
          var __diagRect = null;
          try { __diagRect = el.getBoundingClientRect(); } catch (rectErr) {}
          self.pick(el);
          try {
            // el is now STALE: render() -> renderLists() destroys and
            // rebuilds the whole list on every pick, so el's own
            // aria-checked would just show what it was cloned with BEFORE
            // this click. Re-query the CURRENT node for the row that was
            // supposed to end up selected, by the value actually attempted.
            var freshRow = self.root.querySelector(
              '[data-spp-pick="reason"][data-spp-value="' + __diagValue + '"]'
            );
            console.log('[spp-diag reason-click]', {
              attemptedValue: __diagValue,
              targetTag: __diagTargetTag,
              targetClass: __diagTargetClass,
              rowWidth: __diagRect ? Math.round(__diagRect.width) : null,
              rowHeight: __diagRect ? Math.round(__diagRect.height) : null,
              draftReasonBefore: __diagBefore,
              draftReasonAfter: self.state.draft.reason,
              matchedIntendedValue: self.state.draft.reason === __diagValue,
              freshRowFound: !!freshRow,
              freshRowAriaChecked: freshRow ? freshRow.getAttribute('aria-checked') : null
            });
          } catch (diagErr) {
            console.log('[spp-diag reason-click] logging failed', String(diagErr));
          }
          return;
        }
        self.pick(el); return;
      }
      if ((el = e.target.closest('[data-spp-act]'))) {
        e.preventDefault();
        if (el.getAttribute('aria-disabled') === 'true') return;
        self.act(el.getAttribute('data-spp-act'), el);
        return;
      }
      if ((el = e.target.closest('[data-spp-dev-toggle]'))) {
        var panel = el.closest('[data-spp-dev]');
        panel.setAttribute('data-collapsed', panel.getAttribute('data-collapsed') === 'true' ? 'false' : 'true');
        return;
      }
      if ((el = e.target.closest('[data-spp-dev-status]'))) {
        self.adapter.setStatus(el.getAttribute('data-spp-dev-status')).then(function (sub) {
          self.state.data = sub; self.show('dashboard');
        });
        return;
      }
      if ((el = e.target.closest('[data-spp-dev-pending]'))) {
        self.openSheet('skip');
        self.state.pending = 'skip';
        self.applyPending(true);
        self.render();
        return;
      }
      if (e.target.hasAttribute && e.target.hasAttribute('data-spp-overlay')) self.closeSheet();
    });

    this.root.addEventListener('change', function (e) {
      var date = e.target.closest('[data-spp-date]');
      if (date) {
        self.state.draft.date = date.value || null;
        self.render();
        return;
      }
      var sel = e.target.closest('[data-spp-dev-currency]');
      if (!sel || !self.adapter.setCurrency) return;
      self.adapter.setCurrency(sel.value).then(function () { return self.load(); });
    });

    // `change` alone fires late on some mobile pickers, leaving the summary
    // and the button label stale after a date has visibly been chosen.
    this.root.addEventListener('input', function (e) {
      var note = e.target.closest('[data-spp-note]');
      if (note) {
        // The value is held in state and never written back to the field —
        // re-setting it on every keystroke would fight the caret. Only the
        // validation message is re-rendered.
        self.state.draft.note = note.value || '';
        var errEl = self.root.querySelector('[data-spp-reason-error]');
        if (errEl && !errEl.hidden && !self.reasonProblem()) {
          errEl.hidden = true;
          errEl.textContent = '';
          self.state.reasonError = null;
        }
        if (self.state.reasonSaveError) {
          self.state.reasonSaveError = null;
          if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
        }
        return;
      }
      var retainDate = e.target.closest('[data-spp-retain-date]');
      if (retainDate) {
        self.state.draft.retainDate = retainDate.value || '';
        self.state.draft.retainTile = null;
        self.state.retainError = null;
        self.render();
        return;
      }
      var date = e.target.closest('[data-spp-date]');
      if (!date) return;
      self.state.draft.date = date.value || null;
      self.render();
    });

    this.root.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-spp-form]');
      if (!form) return;
      e.preventDefault();
      if (form.getAttribute('data-spp-form') === 'login') self.act('sendLink', form);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.state.sheet && !self.state.pending) { self.closeSheet(); return; }
      if (e.key === 'Tab' && self.state.sheet) self.trapFocus(e);
    });

    this.syncVetVideo();
    this.bindVetVideoControls();
    window.addEventListener('resize', function () { self.syncVetVideo(); });
  };

  /**
   * Cancel step 2's veterinarian video ships two real files, one per
   * viewport (16:9 desktop/tablet, 1:1 mobile). Picking the file here, in
   * JS, is deliberate: the browser's own <source media> selection — what
   * shipped before this fix — was not reliable on real devices, where
   * both files could end up loaded and visibly overlapping. A single
   * <video> with a plain `src` this method sets cannot have that failure
   * mode, because there is only ever one src, and the OTHER file's URL
   * is never assigned to anything — never fetched, on either viewport.
   *
   * The poster is kept in sync the same way, from a genuinely blank
   * start (see spp-screen-cancel.liquid — there is no static `poster`
   * attribute to correct away from any more): whichever real file's own
   * actual first frame matches the file about to load.
   *
   * Neither viewport autoplays: the video stays paused on its poster,
   * with only the custom .spp__vet-video-play button visible, until the
   * customer taps it (see bindVetVideoControls). Muted here just means
   * "silent if anything ever plays it before that tap" — the tap itself
   * is what turns sound on.
   *
   * Safe to call any time (bind(), and again on resize): it never
   * touches a video the customer has already started — currentTime > 0
   * or a non-paused element means playback is underway, and the file
   * that is already loading is the one that stays.
   */
  Portal.prototype.syncVetVideo = function () {
    var video = this.root.querySelector('.spp__vet-video-el');
    if (!video) return;
    var wantDesktop = window.matchMedia('(min-width: 1024px)').matches;
    var wanted = wantDesktop ? video.dataset.sppVetSrcDesktop : video.dataset.sppVetSrcMobile;
    if (!wanted || video.dataset.sppVetSrcActive === wanted) return;
    if (!video.paused || video.currentTime > 0) return;
    video.dataset.sppVetSrcActive = wanted;
    video.poster = wantDesktop ? video.dataset.sppVetPosterDesktop : video.dataset.sppVetPosterMobile;
    video.muted = true;
    video.src = wanted;
    video.load();
  };

  /**
   * Wires the custom play button drawn over the vet video's poster (see
   * spp-screen-cancel.liquid). Native `controls` is deliberately absent
   * from the markup so nothing but that button shows before the
   * customer's own tap; the tap is also the user gesture that lets JS
   * turn sound on, which an autoplay call could never do reliably.
   *
   * On tap: unmute at normal volume, reveal native controls, hide the
   * custom button, start playback. On 'ended': hand the video back to
   * its poster state — `load()` re-arms the poster (a played element
   * would otherwise keep showing its last frame, not the poster, once
   * paused) — remove native controls, and bring the custom button back,
   * exactly like before the first tap.
   */
  Portal.prototype.bindVetVideoControls = function () {
    var container = this.root.querySelector('.spp__vet-video');
    if (!container) return;
    var video = container.querySelector('.spp__vet-video-el');
    var playBtn = container.querySelector('.spp__vet-video-play');
    if (!video || !playBtn) return;

    playBtn.addEventListener('click', function () {
      video.muted = false;
      video.volume = 1;
      video.setAttribute('controls', '');
      playBtn.hidden = true;
      var playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') playAttempt.catch(function () {});
    });

    video.addEventListener('ended', function () {
      video.removeAttribute('controls');
      video.muted = true;
      video.load();
      playBtn.hidden = false;
    });
  };

  Portal.prototype.trapFocus = function (e) {
    var host = this.root.querySelector('[data-spp-sheet-host]');
    if (!host) return;
    var f = this.focusablesIn(host);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  Portal.prototype.pick = function (el) {
    var kind = el.getAttribute('data-spp-pick');
    var d = this.state.draft;
    var value = el.dataset.sppValue;

    /* One chain, and every branch assigns. A bare `if` inserted mid-chain once
     * severed this: `else if (kind === 'reason')` became the else-branch of an
     * `if (kind === 'reason')`, so the assignment below it could never run and
     * no reason could ever be selected. Keep the clearing INSIDE its branch. */
    if (kind === 'delay') d.delay = value === 'custom' ? 'custom' : parseInt(value, 10);
    else if (kind === 'gap') d.gap = value;
    else if (kind === 'reason') {
      // A different reason is a different recommendation: any half-chosen
      // step-6 adjustment belongs to the old one and must not carry over.
      if (d.reason !== value) { d.retainAdj = null; d.retainTile = null; d.retainDate = ''; }
      d.reason = value;
      // A standing complaint must not outlive the problem it describes.
      this.state.reasonError = null;
      this.state.reasonSaveError = null;
    }
    else if (kind === 'restart') d.restart = parseInt(el.dataset.sppIndex, 10);
    else if (kind === 'startedCategory') d.startedCategory = value;
    else if (kind === 'retainAdj') {
      d.retainAdj = value;
      d.retainTile = null;
      d.retainDate = '';
      this.state.retainError = null;
    }
    else if (kind === 'retainTile') {
      d.retainTile = value;
      if (d.retainAdj === 'pause') {
        d.retainDate = NS.dates.addMonths(this.rescheduleBounds().min, parseInt(value.slice(1), 10));
      }
      this.state.retainError = null;
    }
    this.render();
  };

  /* -----------------------------------------------------------------
   * Actions — one per supported Phoenix operation, plus auth and loyalty
   * ----------------------------------------------------------------- */

  /**
   * Actions that change billing, and may therefore run at most once per
   * opened confirmation. Navigation and sheet-opening are not among them.
   */
  /**
   * Cancellation Retention V2.
   *
   * The nine reasons, and the five schedule changes that can actually be
   * performed. Codes are stable and shared with the server: the label is what
   * the customer reads, the code is what analysis groups on, and changing a
   * label must never silently re-bucket a year of answers.
   */
  var REASONS = [
    ['price', 'Too expensive'],
    ['too_much', 'I have too much product'],
    ['not_using', 'I\u2019m not using it enough'],
    ['no_results', 'I didn\u2019t see the results I expected'],
    ['dislike', 'My dog doesn\u2019t like it'],
    ['not_needed', 'My pet no longer needs it'],
    ['order_issue', 'I had an issue with my order'],
    ['break', 'Just taking a break'],
    ['other', 'Something else']
  ];

  /**
   * Cancel step 3, "why you started" — the approved Portal V2 categories,
   * verbatim. Never sent anywhere and never bucketed like REASONS above;
   * see the 'startedCategories' listData case.
   */
  var STARTED_CATEGORIES = [
    ['dental', 'Dental care', 'Bad breath, plaque or tartar buildup'],
    ['eye', 'Eye care', 'Tear stains, discharge or recurring buildup'],
    ['ear', 'Ear care', 'Odor, dirt or wax buildup'],
    ['paw', 'Paw care', 'Mud, dirt or regular paw cleaning'],
    ['skin', 'Skin & coat care', 'Dirt, odor or keeping your dog fresh between baths'],
    ['other', 'Something else', '']
  ];

  /**
   * Cancel step 2's two real customer reviews — reproduces the approved
   * Portal V2 design's own review carousel, whose source comments describe
   * it as "genuine reviews lifted verbatim from the live VetPets homepage
   * reviews module." That design picks reviews by a single hardcoded mock
   * product; this generalizes the same idea to the real subscription's
   * actual line items (see the 'cancelReviews' listData case below), so
   * nothing here is invented for a product the customer does not have.
   */
  var REVIEW_BANK = {
    freshwipes: {
      category: 'Dental',
      quotes: [
        { name: 'Marilu P.', quote: 'My Pom loves the wipes! Way less hassle than when I used to brush her teeth. Thank you!' },
        { name: 'Lynn H.', quote: 'My two 8 month old dogs are really good at having their teeth cleaned. I can get to their back teeth and all around their mouth.' }
      ]
    },
    eyewipes: {
      category: 'Eyes',
      quotes: [
        { name: 'Pauline L.', quote: 'The wipes softens the crusty buildup over night and makes it easier to remove. Eyes are looking much cleaner and Argo is much happier.' },
        { name: 'Barbara M.', quote: 'These are the only things I have used, which are many, that has actually worked. Very happy' }
      ]
    }
  };

  /** Every option maps to an operation proven end to end against Phoenix. */
  var GAP_OPTIONS = [
    ['skip', 'Skip the next delivery', 'Nothing ships. The one after is unchanged.'],
    ['d7', 'Push it back 7 days', 'Moves the next delivery a week later'],
    ['d15', 'Push it back 15 days', 'Moves it just over two weeks'],
    ['d30', 'Push it back 30 days', 'Moves it a full month'],
    ['date', 'Choose a new delivery date', 'Pick any date that suits you']
  ];

  /** The approved figures, stated once. */
  var OFFER_PERCENT = 50;
  var STANDARD_PERCENT = 20;

  /** How much the customer must actually write for "Something else". */
  var MIN_REASON_NOTE = 3;

  /* =================================================================
   * Step 6 — "Adjust your routine" (reason-personalized)
   * =================================================================
   * reason code -> [kind, action key]. The action key is what tracking
   * records as the recommendation shown (retention_event.offer_type).
   * `next_delivery_40` is the historical offer key and is reused unchanged
   * for the 40% offer so no old metric is redefined.
   *
   * The four legacy reasons the approved design does not list (not_using,
   * not_needed, order_issue, break) are preserved and share the approved
   * "adjustment options" recommendation.
   *
   * 'price' has no entry: the 40% offer is no longer "Too expensive"'s
   * recommendation on THIS screen (see cancel-savings for where it now
   * lives, unconditionally, for every reason). retainKind() below falls
   * through to the same 'note'/'adjust_options' default every other
   * unmapped reason gets.
   */
  var RETAIN_BY_REASON = {
    too_much: ['delay', 'move_delivery_30'],
    no_results: ['keep', 'keep_results'],
    dislike: ['easier', 'routine_guidance'],
    other: ['note', 'adjust_options'],
    not_using: ['note', 'adjust_options'],
    not_needed: ['note', 'adjust_options'],
    order_issue: ['note', 'adjust_options'],
    'break': ['note', 'adjust_options']
  };

  /** Phoenix can move the next billing date; it cannot change a frequency. */
  var RETAIN_MOVE_DAYS = 30;

  /** Secondary adjustments — the SAME four rows for every reason. */
  var RETAIN_ADJUSTMENTS = [
    ['skip', 'Skip next delivery'],
    ['move', 'Move next delivery'],
    ['freq', 'Deliver less often'],
    ['pause', 'Pause RoutineCare']
  ];
  var RETAIN_ADJ_ACTION = {
    skip: 'skip_delivery', move: 'move_delivery', freq: 'move_delivery', pause: 'pause_until_date'
  };

  var RETAIN_STAGE_LABELS = ['Weeks 1–2', 'Weeks 2–3', 'Days 60–90'];

  /** Care-goal stories — verbatim from the approved design (step 3 answer). */
  var CARE_STORIES = {
    dental: {
      label: 'Dental care', img: true, note: 'FreshWipes',
      noteTail: ' works along the gum line — the same 30 seconds each day is what keeps the progress going.',
      stages: ['Your dog becomes more comfortable having their mouth handled.', 'Early improvement in breath and surface plaque may begin.', 'Meaningful dental results build through consistent daily use.']
    },
    eye: {
      label: 'Eye care', img: true, note: 'EyeWipes',
      noteTail: ' works best used daily, before buildup has a chance to settle into the fur.',
      stages: ['Your dog becomes more comfortable with wiping around the eyes.', 'Early improvement in tear-stain buildup may begin.', 'Meaningful results around the eye area build through consistency.']
    },
    ear: {
      // No before/after image for Ear: none supplied, so none is requested.
      label: 'Ear care', img: false, note: 'EarWipes',
      noteTail: ' works best on a steady weekly rhythm, and always after water.',
      stages: ['Your dog becomes more comfortable having their ears handled.', 'Early improvement in ear cleanliness may begin.', 'Meaningful ear results build through consistent weekly care.']
    },
    skin: {
      label: 'Skin & coat care', img: false, note: 'GloveWipes',
      noteTail: ' works best straight after walks, as part of coming back indoors.',
      stages: ['Your dog becomes more comfortable with the wipe-down routine.', 'Early improvement in coat cleanliness and freshness may begin.', 'Meaningful coat and skin results build through consistency.']
    },
    paw: {
      label: 'Paw care', img: false, note: 'Paw Foam',
      noteTail: ' works best at the door, every time — that regularity is what does the work.',
      stages: ['Your dog becomes more comfortable having their paws handled.', 'Early improvement in paw cleanliness may begin.', 'Meaningful paw results build through consistency.']
    },
    other: {
      label: 'Long-term care', img: false, note: null,
      stages: ['Your dog becomes more comfortable with the routine.', 'Early improvements may begin.', 'Meaningful results build through consistency.']
    }
  };

  /** "Try these first" tips per product — verbatim from the approved design. */
  var RETAIN_TIPS = {
    fresh: ['Start with one tooth, not the whole mouth, and build up over a week.', 'Let them sniff and lick the wipe before it goes near their teeth.', 'Try it after exercise, when they are calm rather than excited.'],
    eye: ['Warm the wipe in your hand first so it is not a cold surprise.', 'Come from below the eye line rather than head-on.', 'One eye per session for the first week.'],
    ear: ['Hold the ear flap open and wipe in one slow movement.', 'Pair it with something they already like — a brush, or a treat straight after.', 'Little and often beats one long session.'],
    glove: ['Start with the back and shoulders and leave the paws until later.', 'Keep the first sessions to about twenty seconds.', 'Do it while they are eating, so it happens around something good.'],
    paw: ['One paw at a time, front paws first.', 'Foam into your own hand first so the sound is not a surprise.', 'Reward immediately after the last paw, every time.']
  };

  /**
   * Why the reason screen cannot continue yet, or null when it can.
   *
   * "Something else" without words is the same as no reason at all — it tells
   * retention analysis nothing, and it is the one option that exists purely to
   * capture what the fixed list could not.
   */
  Portal.prototype.reasonProblem = function () {
    var d = this.state.draft;
    if (!d.reason) return 'Choose a reason so we can continue.';
    if (d.reason === 'other') {
      var note = (d.note || '').trim();
      if (note.length < MIN_REASON_NOTE) return 'Tell us briefly what happened.';
    }
    return null;
  };

  /** setTimeout as a promise — short, bounded backoff between retries only. */
  function sppWait(ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); }

  /**
   * Deliver the reason reliably before leaving the reason screen.
   *
   * THE BUG THIS EXISTS FOR
   * ------------------------
   * Production data showed a real fraction of customers' cancellation
   * reasons never reaching cancellation_reason at all — this used to be a
   * pure fire-and-forget call the customer's navigation never waited on.
   * Now: awaited, with a short bounded retry, before the customer moves on.
   *
   * RETRY KEYS, PRECISELY
   * ----------------------
   * attemptKey('reasonContinue') survives exactly as long as it needs to: a
   * NETWORK failure (the request never reached the server, or its response
   * never came back) keeps the SAME key, so a retry either lands as the
   * first real attempt or safely replays a success that already happened.
   * Any DEFINITIVE server response — even a failure — means the server's
   * own idempotency claim has already settled under that key permanently
   * (the same key can never succeed twice), so that response mints a FRESH
   * key before the next attempt. `invalid_journey` additionally drops the
   * held journey id, so a stale/foreign one is never resent.
   *
   * Two short backoffs (300ms, 700ms) — a healthy network never notices;
   * the worst case is under a second before the customer sees a retry
   * affordance, never a silent, unrecoverable loss.
   *
   * Advances to cancel-offer, not cancel-alt: the approved Portal V2 order
   * has the reason screen at step 5, after alternatives (step 4), so a
   * saved reason leads straight to the retention offer.
   */
  Portal.prototype.submitReason = function (reasonCode, noteText) {
    var self = this;
    if (this.state.pending) return; // one attempt at a time, same guard as run()

    this.state.reasonSaveError = null;
    this.state.pending = 'reasonContinue';
    this.applyPending(true);
    this.render();

    var RETRY_DELAYS = [300, 700];

    function isNetworkFailure(err) { return !!err && err.code === 'network'; }

    function attempt(n, journeyId) {
      // attemptKey() persists in state.attempts until releaseAttempt() runs,
      // so a network failure below (which does NOT release it) hands the
      // exact same key to the next call — a safe replay if the server's
      // claim actually landed, a fresh first attempt if it never arrived.
      var key = self.attemptKey('reasonContinue');
      return self.adapter.recordCancelReason(reasonCode, noteText, {
        idempotencyKey: key,
        journeyId: journeyId
      }).then(function (result) {
        self.releaseAttempt('reasonContinue');
        // The reason write wins ties over a beacon that got there first —
        // both name the same journey once either succeeds, but only one
        // of them needs to be adopted.
        if (result && result.journeyId) self.state.retentionJourneyId = result.journeyId;
        self.state.pending = null;
        self.applyPending(false);
        self.show('cancel-offer');
      }, function (err) {
        // Any DEFINITIVE server response — even a failure — means that key
        // is now permanently spent server-side (the same key can never
        // succeed twice); release it so the NEXT attemptKey() call mints a
        // fresh one. A pure network failure keeps it, on purpose.
        if (!isNetworkFailure(err)) self.releaseAttempt('reasonContinue');
        var nextJourneyId = (err && err.code === 'invalid_journey') ? null : journeyId;
        if (n < RETRY_DELAYS.length) {
          return sppWait(RETRY_DELAYS[n]).then(function () { return attempt(n + 1, nextJourneyId); });
        }
        // Retries exhausted. Never pretend this saved: the selection stays
        // exactly as the customer left it (state.draft is untouched), and
        // the journey id survives so a manual retry continues this SAME
        // attempt rather than starting an unrelated one.
        self._reasonRetryJourneyId = nextJourneyId;
        self.state.pending = null;
        self.applyPending(false);
        self.state.reasonSaveError = 'We could not save that — check your connection and tap Continue to try again.';
        self.render();
      });
    }

    var startJourneyId = ('_reasonRetryJourneyId' in this) ? this._reasonRetryJourneyId : this.state.retentionJourneyId;
    delete this._reasonRetryJourneyId;
    attempt(0, startJourneyId);
  };

  /* =================================================================
   * Step 6 — "Adjust your routine": content, targets, verification
   * ================================================================= */

  /** Which recommendation the chosen reason gets, and its tracking key. */
  Portal.prototype.retainKind = function () {
    var d = this.state.draft;
    var r = d && RETAIN_BY_REASON[d.reason];
    return r ? { kind: r[0], action: r[1] } : { kind: 'note', action: 'adjust_options' };
  };

  /**
   * Best-effort retention beacon. Never blocks, never a mutation. Only ever
   * on the journey this visit holds: sent without one, the server would
   * attach it to the customer's newest open journey, possibly a stale one.
   */
  Portal.prototype.trackRetention = function (eventType, action) {
    if (!this.adapter || !this.adapter.recordRetentionEvent) return;
    if (!this.state.retentionJourneyId) return;
    this.adapter.recordRetentionEvent(eventType, this.state.retentionJourneyId, action)
      .catch(function () {});
  };

  /**
   * Resolve the cancellation journey id for a save, opening the journey when
   * none is held yet (the Longer Gap step precedes the reason screen).
   * Resolves the id, or null — never rejects: a failed analytics write must
   * never block the customer's schedule change.
   */
  Portal.prototype.ensureRetentionJourney = function () {
    var self = this;
    if (this.state.retentionJourneyId) return Promise.resolve(this.state.retentionJourneyId);
    if (!this.adapter || !this.adapter.recordRetentionEvent) return Promise.resolve(null);
    return Promise.resolve(this.adapter.recordRetentionEvent('cancellation_started', null))
      .then(function (result) {
        var minted = result && typeof result.journeyId === 'string' && result.journeyId ? result.journeyId : null;
        if (minted && !self.state.retentionJourneyId) self.state.retentionJourneyId = minted;
        return self.state.retentionJourneyId || minted;
      }, function () {
        return self.state.retentionJourneyId || null;
      });
  };

  function retainProductKey(title) {
    var t = String(title || '').toLowerCase();
    return t.indexOf('fresh') !== -1 ? 'fresh'
      : t.indexOf('eye') !== -1 ? 'eye'
      : t.indexOf('ear') !== -1 ? 'ear'
      : t.indexOf('glove') !== -1 ? 'glove'
      : t.indexOf('paw') !== -1 ? 'paw'
      : null;
  }

  /**
   * Everything the step-6 card shows for the current reason. Pure of side
   * effects, so tests can call it directly.
   */
  Portal.prototype.retainContent = function () {
    var sub = this.state.data;
    var d = this.state.draft;
    var k = this.retainKind();
    var lines = (sub && sub.lines) || [];
    var P = lines.length === 1 ? lines[0].title.replace(/ (jar|pack).*$/, '') : 'Routine Care';
    var next = sub && sub.nextOrderDate;
    var freq = sub && sub.intervalDays;
    var out = {
      kind: k.kind, action: k.action, title: '', body: '', points: [], tips: [], timeline: [],
      cta: 'Keep my RoutineCare active', foot: '',
      careLabel: '', productNote: '', review: null, imageKey: null,
      dateNow: next ? this.fmtDate(next, 'short') : '', dateAfter: ''
    };

    if (k.kind === 'delay') {
      var moved = next ? NS.dates.addDays(next, RETAIN_MOVE_DAYS) : null;
      out.dateAfter = moved ? this.fmtDate(moved, 'short') : '';
      out.title = 'Move your next delivery ' + RETAIN_MOVE_DAYS + ' days later';
      out.body = 'You receive ' + P + (freq ? ' every ' + freq + ' days' : ' regularly') +
        '. Moving your next delivery ' + RETAIN_MOVE_DAYS + ' days later gives you time to get through the jars you already have before the next one comes. The routine itself carries on exactly as it is.';
      out.points = [
        'One delivery moves — same product, same RoutineCare price and free shipping.',
        'Later deliveries follow from your new date. Your delivery cadence is not changed.',
        'Skip, choose another date or pause below instead.'
      ];
      out.cta = 'Move my next delivery ' + RETAIN_MOVE_DAYS + ' days later';
      out.foot = 'Takes effect immediately. Nothing ships today.';
    } else if (k.kind === 'keep') {
      var cat = CARE_STORIES[d.startedCategory] ? d.startedCategory : 'other';
      var story = CARE_STORIES[cat];
      out.title = 'Give the routine time to work';
      out.body = 'Progress builds through consistent use — and you’re protected for 100 days.';
      out.careLabel = story.label;
      out.timeline = story.stages.map(function (body, i) { return { label: RETAIN_STAGE_LABELS[i], body: body }; });
      out.productNote = story.note
        ? story.note + story.noteTail
        : 'A steady ' + P + ' routine is what keeps long-term care working — little and often, rather than in bursts.';
      // Only Dental and Eye have a verified review. None is invented for the rest.
      var bank = cat === 'dental' ? REVIEW_BANK.freshwipes : cat === 'eye' ? REVIEW_BANK.eyewipes : null;
      if (bank) out.review = { quote: bank.quotes[0].quote, name: bank.quotes[0].name, category: bank.category };
      out.imageKey = story.img ? cat : null;
      out.foot = 'No change to your delivery date or your price.';
    } else if (k.kind === 'easier') {
      out.title = 'Make the ' + P + ' routine easier for your dog';
      out.body = 'Most dogs need a week or two to accept something new. Shorter, calmer sessions usually work better than pushing through the full routine — and you can slow the deliveries down while you build it up.';
      var pk = null;
      for (var li = 0; li < lines.length && !pk; li++) pk = retainProductKey(lines[li].title);
      out.tips = RETAIN_TIPS[pk || 'fresh'];
      out.points = [
        'Nothing about your subscription changes.',
        'Move, skip or pause your next delivery below while you take it slowly.'
      ];
      out.foot = 'If it still is not working, the 100-day guarantee covers you.';
    } else {
      var isOther = d.reason === 'other';
      out.title = isOther ? 'Tell us what would help' : 'There may be a better fit than cancelling';
      out.body = 'There is usually an adjustment that fits better than cancelling — a longer gap, a skipped delivery, or a pause. ' +
        (isOther ? 'Tell us what is going on and keep your routine active in the meantime.' : 'Keep your routine active in the meantime.');
      out.points = [
        'All four adjustments below are open to you.',
        'Your RoutineCare price, free shipping and 100-day guarantee stay as they are.'
      ];
      out.foot = 'You can still continue to cancellation at any point.';
    }
    return out;
  };

  /**
   * The billing date a secondary adjustment would produce, or null while it
   * is not yet determined. Skip is one interval; move/frequency is 7/15/30
   * days (the server-permitted delays); pause is a date the customer picks.
   */
  Portal.prototype.retainAdjTarget = function () {
    var sub = this.state.data;
    var d = this.state.draft;
    if (!sub || !sub.nextOrderDate) return null;
    if (d.retainAdj === 'skip') {
      return sub.intervalDays > 0 ? NS.dates.addDays(sub.nextOrderDate, sub.intervalDays) : null;
    }
    if (d.retainAdj === 'move' || d.retainAdj === 'freq') {
      var n = d.retainTile ? parseInt(d.retainTile.slice(1), 10) : 0;
      return n > 0 ? NS.dates.addDays(sub.nextOrderDate, n) : null;
    }
    if (d.retainAdj === 'pause') return d.retainDate || null;
    return null;
  };

  /** Why the chosen pause date cannot be submitted, or null. */
  Portal.prototype.retainAdjProblem = function () {
    var sub = this.state.data;
    var d = this.state.draft;
    if (d.retainAdj !== 'pause') return null;
    if (!d.retainDate) return 'Choose the date your deliveries should resume.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.retainDate)) return 'That date could not be read.';
    var b = this.rescheduleBounds();
    if (d.retainDate < b.min) return 'Choose a date from today onwards.';
    if (d.retainDate > b.max) return 'Choose a date within the next year.';
    if (sub && sub.nextOrderDate && d.retainDate <= sub.nextOrderDate) {
      return 'Choose a date after your current next delivery (' + this.fmtDate(sub.nextOrderDate, 'short') + ').';
    }
    return null;
  };

  /**
   * THE PERSISTENCE CHECK. A schedule change is only reported as done when a
   * FRESH read of the subscription shows the state the customer asked for:
   * still active, next billing date exactly the target. Anything else throws
   * `unverified`, which the customer sees as a clear, non-destructive message
   * — and which never counts as a save.
   */
  Portal.prototype.retainVerify = function (targetIso, result) {
    var self = this;
    var fresh = this.adapter.refetchSubscription
      ? this.adapter.refetchSubscription()
      : Promise.resolve(result);
    return fresh.then(function (st) {
      var ok = st && st.status === 'active' && st.nextOrderDate === targetIso;
      if (!ok) throw NS.PortalError('unverified', 'We could not confirm that change.');
      self.state.data = st;
      return st;
    });
  };

  /**
   * ONE mutation, whichever button started it. `doMutation(opts)` performs
   * exactly one adapter write (skip / delay / reschedule) — the same proven
   * update-next-billing-date operation the dashboard uses — carrying the
   * retention journey id and action key so the server, and only the server,
   * records the save after Phoenix confirms.
   */
  Portal.prototype.retainRun = function (pendingKey, action, targetIso, doMutation) {
    var self = this;
    var sub = this.state.data;
    if (!sub || this.state.pending) return;
    this.state.retainError = null;
    var before = sub.nextOrderDate;
    var retainJourneyId = this.state.retentionJourneyId;
    this.trackRetention('recommendation_attempted', action);

    this.run(pendingKey, function (attemptKey) {
      return doMutation({
        idempotencyKey: attemptKey,
        expectedNextBillingDate: before,
        retentionJourneyId: retainJourneyId,
        retentionAction: action
      }).then(function (result) {
        // The write applied but the server could not re-read Phoenix: the
        // change is real, but it is NOT verified — run() shows the refresh
        // prompt and this is not reported as a completed save.
        if (result && result.refreshRequired) return result;
        return self.retainVerify(targetIso, result);
      });
    }, {
      attempt: pendingKey,
      // run() hands these (state, result): the verified subscription is `result`.
      then: function (state, result) {
        state.retainSaved = { action: action, next: result && result.nextOrderDate };
        // The journey is now resolved as a save; a later cancel attempt in
        // this same visit must open a new one.
        if (retainJourneyId && self.state.retentionJourneyId === retainJourneyId) {
          self.state.retentionJourneyId = null;
        }
        self.show('dashboard');
      },
      toast: function (state, result) {
        var nx = result && result.nextOrderDate;
        return nx ? 'Done — your next delivery is ' + self.fmtDate(nx, 'medium') : 'Your delivery has been updated';
      }
    });
  };

  /** Render step 6: which recommendation block, which panel, what errors. */
  Portal.prototype.renderRetain = function () {
    var card = this.root.querySelector('[data-spp-retain]');
    if (!card) return;
    var d = this.state.draft;
    var c = this.retainContent();
    var i;

    // Step-6 text fields. Filled here, not in viewModel(), so the shared view
    // model stays free of step-6 logic.
    var sub = this.state.data;
    var s = this.state;
    var vm = {};
    var rc = c;
    vm['retain.title'] = rc.title;
    vm['retain.body'] = rc.body;
    vm['retain.cta'] = s.pending === 'acceptOffer' || s.pending === 'retainPrimary' ? 'Working\u2026' : rc.cta;
    vm['retain.foot'] = rc.foot;
    vm['retain.dateNow'] = rc.dateNow;
    vm['retain.dateAfter'] = rc.dateAfter;
    vm['retain.careLabel'] = rc.careLabel;
    vm['retain.productNote'] = rc.productNote;
    vm['retain.reviewQuote'] = rc.review ? '\u201C' + rc.review.quote + '\u201D' : '';
    vm['retain.reviewName'] = rc.review ? rc.review.name : '';
    vm['retain.reviewCategory'] = rc.review ? rc.review.category : '';
    var adjTarget = this.retainAdjTarget();
    vm['retain.adjFrom'] = sub && sub.nextOrderDate ? this.fmtDate(sub.nextOrderDate, 'short') : '';
    vm['retain.adjTo'] = adjTarget ? this.fmtDate(adjTarget, 'short') : '';
    var adjLeads = {
      skip: 'Nothing ships on your next delivery date. Your routine picks up again with the delivery after \u2014 you will not be charged for the skipped one.',
      move: 'Pick how far to move your next delivery. Later deliveries follow from the new date.',
      freq: 'Your recurring schedule cannot be changed from the portal yet, so this moves your next delivery later instead. Your delivery cadence itself stays as it is.',
      pause: 'Nothing ships or is charged until the date you choose, when your deliveries resume. This moves your next delivery to that date and keeps your subscription active.'
    };
    vm['retain.adjLead'] = adjLeads[d.retainAdj] || '';
    vm['label.retainAdjust'] = s.pending === 'retainAdjust' ? 'Updating\u2026' : (
      d.retainAdj === 'skip' ? 'Skip this delivery'
      : d.retainAdj === 'pause' ? (adjTarget ? 'Pause until ' + this.fmtDate(adjTarget, 'short') : 'Choose a resume date')
      : (adjTarget ? 'Move to ' + this.fmtDate(adjTarget, 'short') : 'Choose how far'));
    var fields = card.querySelectorAll('[data-spp-field]');
    for (i = 0; i < fields.length; i++) {
      var fk = fields[i].getAttribute('data-spp-field');
      if (Object.prototype.hasOwnProperty.call(vm, fk)) fields[i].textContent = vm[fk];
    }
    var panelEl = this.root.querySelector('[data-spp-retain-panel]');
    if (panelEl) {
      var pf = panelEl.querySelectorAll('[data-spp-field]');
      for (i = 0; i < pf.length; i++) {
        var pk = pf[i].getAttribute('data-spp-field');
        if (Object.prototype.hasOwnProperty.call(vm, pk)) pf[i].textContent = vm[pk];
      }
    }

    var blocks = card.querySelectorAll('[data-spp-retain-block]');
    for (i = 0; i < blocks.length; i++) {
      blocks[i].hidden = blocks[i].getAttribute('data-spp-retain-block') !== c.kind;
    }

    var review = card.querySelector('[data-spp-retain-review]');
    if (review) review.hidden = !c.review;

    var err = card.querySelector('[data-spp-retain-error]');
    if (err) {
      err.textContent = this.state.retainError || '';
      err.hidden = !this.state.retainError;
    }

    this.syncBeforeAfter(card, c);

    var panel = this.root.querySelector('[data-spp-retain-panel]');
    if (!panel) return;
    panel.hidden = !d.retainAdj;
    if (!d.retainAdj) return;

    var tiles = panel.querySelector('[data-spp-retain-tiles]');
    if (tiles) tiles.hidden = d.retainAdj === 'skip';
    var dateWrap = panel.querySelector('[data-spp-retain-date-wrap]');
    if (dateWrap) {
      dateWrap.hidden = d.retainAdj !== 'pause';
      var input = dateWrap.querySelector('[data-spp-retain-date]');
      if (input && d.retainAdj === 'pause') {
        var b = this.rescheduleBounds();
        input.min = b.min;
        input.max = b.max;
        if (document.activeElement !== input) input.value = d.retainDate || '';
      }
    }

    var target = this.retainAdjTarget();
    var fromTo = panel.querySelector('[data-spp-retain-fromto]');
    if (fromTo) fromTo.hidden = !target;

    var problem = this.retainAdjProblem();
    var pErr = panel.querySelector('[data-spp-retain-adj-error]');
    if (pErr) {
      var msg = (d.retainAdj === 'pause' && d.retainDate && problem) ? problem : '';
      pErr.textContent = msg;
      pErr.hidden = !msg;
    }
  };

  /**
   * Dental / Eye / Ear may show an approved before/after image; Coat, Paw and
   * Other never do. The column stays hidden until the image has really loaded,
   * so a missing asset can never leave an empty slot or a placeholder — the
   * timeline simply stays full width.
   */
  Portal.prototype.syncBeforeAfter = function (card, c) {
    var col = card.querySelector('[data-spp-ba]');
    var img = card.querySelector('[data-spp-ba-img]');
    if (!col || !img) return;
    var key = c.kind === 'keep' ? c.imageKey : null;
    if (!key) {
      col.hidden = true;
      img.removeAttribute('src');
      img.removeAttribute('data-spp-ba-key');
      return;
    }
    if (img.getAttribute('data-spp-ba-key') === key) return;
    img.setAttribute('data-spp-ba-key', key);
    col.hidden = true;
    var base = card.getAttribute('data-spp-asset-base') || '';
    var url = base.replace(/spp-cancel-benefits-mobile\.jpg/, 'spp-before-after-' + key + '.jpg');
    if (!url || url === base) return;
    img.onload = function () {
      if (img.getAttribute('data-spp-ba-key') === key) {
        img.alt = 'Before and after ' + c.careLabel.toLowerCase();
        col.hidden = false;
      }
    };
    img.onerror = function () { col.hidden = true; };
    img.src = url;
  };

  var CONFIRMED_ACTIONS = { skip: 1, delay: 1, reschedule: 1, cancel: 1, reactivate: 1 };

  Portal.prototype.act = function (name, el) {
    var self = this, s = this.state, d = s.draft, sub = s.data;
    var id = sub ? sub.id : null;

    // ONE CONFIRMATION, AT MOST ONE MUTATION.
    //
    // Checked here rather than in the click handler so it holds however act()
    // was reached — a click, a keyboard activation, a form submit, or a second
    // listener nobody meant to attach.
    if (CONFIRMED_ACTIONS[name]) {
      // Already in flight. The spinner on the button is the answer; saying
      // anything else here would talk over it.
      if (s.pending) return;

      if (s.confirmSpent) {
        // NEVER SILENT.
        //
        // A refusal the customer cannot see is indistinguishable from a broken
        // button, and a customer who thinks a button is broken presses it
        // again. That is precisely what happened: the guard refused every
        // press of "Yes, cancel my subscription" without a word.
        this.toast('That has already been submitted — refresh to see the latest.');
        return;
      }
      s.confirmSpent = true;
    }

    switch (name) {

      case 'close':
        this.closeSheet();
        return;

      case 'sendLink': {
        var input = this.root.querySelector('#spp-email');
        var err = this.root.querySelector('[data-spp-error="login"]');
        var value = input ? input.value.trim() : '';
        if (!value || value.indexOf('@') === -1) {
          if (err) { err.textContent = 'Enter the email address you used at checkout.'; err.hidden = false; }
          if (input) input.focus();
          return;
        }
        if (err) err.hidden = true;
        // refresh:false — there is no session yet. See run().
        this.run('sendLink', function () { return self.adapter.requestMagicLink(value); }, {
          closeSheet: false,
          refresh: false,
          then: function () {
            // Neutral by design: the same screen shows whether or not an
            // account exists, so the portal cannot be used to enumerate.
            self.state.customer = self.state.customer || {};
            self.state.customer.email = value;
            self.show('sent');
          }
        });
        return;
      }

      case 'openLink':
        // Mock-only stand-in for clicking the emailed link — the same
        // fresh-authentication event bootLive()'s handoff branch handles in
        // live mode, so it lands on 'welcome' the same way.
        this.show('loading');
        this.load().then(function () { self.show('welcome'); }).catch(function (e) { self.fail(e); });
        return;

      case 'resend':
        // Mock-only control, and refresh:false for the same reason as sendLink.
        this.run('resend', function () {
          return self.adapter.requestMagicLink(s.customer ? s.customer.email : '');
        }, {
          closeSheet: false,
          refresh: false,
          then: function () { self.show('sent'); },
          toast: 'New sign-in link sent'
        });
        return;

      case 'retry':
        // Identical handling to boot: fail() decides sign-in vs error screen,
        // so a dead session can never render as a subscription-read failure.
        this.state.error = null;
        if (!this.hasSession()) { this.fail(NS.PortalError('unauthenticated', '')); return; }
        this.show('loading');
        this.load().then(function () { self.show('dashboard'); }).catch(function (e) { self.fail(e); });
        return;

      /**
       * Re-read VetPoints only. A ledger outage is scoped to this one screen
       * (see the adapter's getLoyalty), so retrying it never touches the
       * subscription, deliveries or cancellation state — no full-portal
       * reload, no loading screen.
       */
      case 'retryLoyalty':
        if (s.loyaltyPending) return;
        s.loyaltyPending = true;
        this.render();
        this.adapter.getLoyalty(true).then(function (loy) {
          s.loyaltyPending = false;
          s.loyalty = loy;
          self.render();
        }, function (err) {
          // The one failure this can still throw is an expired session,
          // which ends the visit exactly like any other read would.
          s.loyaltyPending = false;
          self.fail(err);
        });
        return;

      /* --- POST /update-next-billing-date --- */
      case 'skip': {
        var before = sub.nextOrderDate;
        this.run('skip', function (attemptKey) {
          return self.adapter.skipNextDelivery(id, {
            idempotencyKey: attemptKey,
            // The date on screen when the customer confirmed. The server
            // refuses the request if Phoenix no longer holds it, so a stale
            // duplicate cannot skip a second cycle.
            expectedNextBillingDate: before
          });
        }, {
          attempt: 'skip',
          // Stay on the dashboard and confirm there. The refreshed next
          // delivery date is already on screen behind the toast, which is a
          // better confirmation than a separate screen describing it.
          then: function (st) {
            self.show('dashboard');
          },
          toast: function (st) {
            var next = st.data && st.data.nextOrderDate;
            return next
              ? 'Delivery skipped — next one ' + self.fmtDate(next, 'long')
              : 'Delivery skipped';
          }
        });
        return;
      }

      case 'delay': {
        var custom = this.isCustomDate();

        // A date the server would refuse never leaves the browser. The sheet
        // stays open with the reason shown, so the customer can correct it
        // rather than being told the operation failed.
        if (custom && this.rescheduleError()) {
          s.confirmSpent = false;
          this.render();
          return;
        }

        var chosen = custom ? d.date : null;
        this.run('delay', function (attemptKey) {
          var opts = {
            idempotencyKey: attemptKey,
            // The date on screen when the customer confirmed. Without it the
            // server cannot tell a duplicate from a second, genuine change.
            expectedNextBillingDate: sub.nextOrderDate
          };
          return custom
            ? self.adapter.rescheduleNextDelivery(id, chosen, opts)
            : self.adapter.delayNextDelivery(id, d.delay, opts);
        }, {
          attempt: custom ? 'reschedule' : 'delay',
          then: function () { self.show('dashboard'); },
          toast: function (st) { return 'Delivery moved to ' + self.fmtDate(st.data.nextOrderDate, 'short'); }
        });
        return;
      }

      /* --- Cancellation Retention V2 ------------------------------- */

      /**
       * Step 6's primary button. What it does depends on the recommendation:
       *  - delay  -> move the next delivery 30 days later (verified)
       *  - other  -> "Keep my RoutineCare active": NO Phoenix change, so it is
       *              tracked as kept and is never counted as a save.
       * The 40% offer is no longer one of this screen's recommendations —
       * see cancel-savings' own "Apply X% to my next delivery" button,
       * wired directly to data-spp-act="acceptOffer".
       */
      case 'retainPrimary': {
        var rk = this.retainKind();
        if (rk.kind === 'delay') {
          if (!sub || !sub.nextOrderDate) return;
          var moveTo = NS.dates.addDays(sub.nextOrderDate, RETAIN_MOVE_DAYS);
          this.retainRun('retainPrimary', rk.action, moveTo, function (opts) {
            return self.adapter.delayNextDelivery(id, RETAIN_MOVE_DAYS, opts);
          });
          return;
        }
        // Keep: nothing to write. Recorded, then back to the dashboard.
        this.trackRetention('recommendation_kept', rk.action);
        this.show('dashboard');
        this.toast('Your ' + (sub && sub.lines && sub.lines.length === 1
          ? sub.lines[0].title.replace(/ (jar|pack).*$/, '') : 'RoutineCare') + ' routine continues');
        return;
      }

      /** A secondary adjustment (skip / move / frequency / pause), all via update-next-billing-date. */
      case 'retainAdjust': {
        var adj = d.retainAdj;
        if (!adj || !sub) return;
        var problem2 = this.retainAdjProblem();
        var target2 = this.retainAdjTarget();
        if (problem2 || !target2) {
          this.state.retainError = problem2 || 'Choose an option first.';
          this.render();
          return;
        }
        var adjAction = RETAIN_ADJ_ACTION[adj];
        this.retainRun('retainAdjust', adjAction, target2, function (opts) {
          if (adj === 'skip') return self.adapter.skipNextDelivery(id, opts);
          if (adj === 'pause') return self.adapter.rescheduleNextDelivery(id, target2, opts);
          return self.adapter.delayNextDelivery(id, parseInt(d.retainTile.slice(1), 10), opts);
        });
        return;
      }

      /**
       * Leaving the reason screen. The answer is recorded on the way past,
       * because the customers this journey saves never reach a cancellation
       * and are exactly the ones worth learning from.
       *
       * Recording is best effort: it must never block a customer from
       * continuing, and it is not a mutation.
       */
      case 'reasonContinue': {
        /* Validation first, and OUT LOUD.
         *
         * This used to `return` silently when nothing was selected, which is
         * indistinguishable from a broken button: the customer taps it again
         * and still nothing happens. A refusal the customer cannot see is the
         * same defect that made the cancel button look dead. */
        var problem = this.reasonProblem();
        if (problem) {
          this.state.reasonError = problem;
          this.render();
          var box = this.root.querySelector(
            d.reason === 'other' ? '[data-spp-note]' : '[data-spp-pick="reason"]'
          );
          if (box && box.focus) { try { box.focus(); } catch (e) {} }
          return;
        }

        this.state.reasonError = null;
        this.state.reasonSaveError = null;
        var noteText = d.reason === 'other' ? (d.note || '').trim() : '';

        // Reason now sits at step 5 of the approved Portal V2 order — after
        // alternatives, not before — so a saved reason leads to the
        // retention offer, handled inside submitReason() itself.
        if (!self.adapter.recordCancelReason) { this.show('cancel-offer'); return; }
        this.submitReason(d.reason, noteText);
        return;
      }

      /** One of the five proven schedule changes, chosen on the gap screen. */
      case 'applyGap': {
        if (!d.gap) return;
        var before = sub.nextOrderDate;
        var target = d.gap;

        var gapJourneyId = null;

        this.run('applyGap', function (attemptKey) {
          // The cancellation journey this save belongs to — this is what lets
          // the server mark saved_gap safely, scoped to exactly this
          // cancellation attempt rather than guessing. Since Portal V2 the
          // Longer Gap step (4) comes BEFORE the reason screen (5), which is
          // where cancellation_started normally opens the journey, so on a
          // first pass none is held yet: open it here. Minting happens inside
          // run(), so the pending guard already blocks a double click. The
          // ordinary dashboard buttons call these same adapter methods
          // without this, and never become a "save".
          return self.ensureRetentionJourney().then(function (journeyId) {
            gapJourneyId = journeyId;
            var opts = {
              idempotencyKey: attemptKey,
              expectedNextBillingDate: before,
              retentionJourneyId: journeyId
            };
            if (target === 'skip') return self.adapter.skipNextDelivery(id, opts);
            if (target === 'date') return self.adapter.rescheduleNextDelivery(id, d.date, opts);
            return self.adapter.delayNextDelivery(id, parseInt(target.slice(1), 10), opts);
          });
        }, {
          attempt: 'applyGap',
          then: function () {
            // With a journey id the server attributes saved_gap itself. With
            // none (it could not be opened) nothing is recorded: the id-less
            // recordCancelOutcome would resolve whatever open journey the
            // server finds — possibly a stale one from an earlier visit. A
            // missed save is better than one counted on the wrong journey.
            // That journey is now resolved as saved_gap; a later cancel
            // attempt in this same page session must open a new one.
            if (gapJourneyId && self.state.retentionJourneyId === gapJourneyId) {
              self.state.retentionJourneyId = null;
            }
            self.show('dashboard');
          },
          toast: function (st) {
            var next = st.data && st.data.nextOrderDate;
            return next ? 'Delivery moved to ' + self.fmtDate(next, 'short') : 'Delivery moved';
          }
        });
        return;
      }

      /**
       * The 40% offer. GATED, and honestly so.
       *
       * The request goes out and the server refuses it, rather than the button
       * being disabled or the screen faking a success. The customer learns the
       * truth — it is not available yet — instead of being told a discount
       * applied that the next invoice would contradict.
       */
      case 'acceptOffer': {
        var offerJourneyId = null;
        this.run('acceptOffer', function (attemptKey) {
          // The offer route attributes saved_offer ONLY to the journey sent
          // here — this visit's, opened first if none is held yet. Never one
          // from an earlier visit. Never blocks the offer itself: with no
          // journey the offer still applies, just unattributed.
          return self.ensureRetentionJourney().then(function (journeyId) {
            offerJourneyId = journeyId;
            return self.adapter.acceptRetentionOffer({ idempotencyKey: attemptKey, journeyId: journeyId });
          });
        }, {
          attempt: 'acceptOffer',
          /* THE BUG THIS EXISTS FOR:
           *
           * Every other mutation (skip/delay/reschedule/cancel/reactivate)
           * goes through the adapter's mutate(), which returns the server's
           * fresh re-read subscription — an object carrying `.id` — so
           * run()'s own `if (result && result.id) self.state.data = result`
           * updates the dashboard automatically. acceptRetentionOffer
           * returns {percentOff, offerPrice, verified, ...}, which has no
           * `.id`, so that update never fired: the toast reported the new
           * price correctly while the dashboard kept showing the old one
           * until a full page reload.
           *
           * refreshAuthenticatedData() (run()'s normal post-action refresh)
           * does not fix this either — it re-reads loyalty, the inactive
           * list and deliveries, but never the primary subscription. Only
           * load() re-fetches that, via the SAME getSubscription() call the
           * page makes on open. refresh:false skips the smaller refresh
           * below in favour of load(), which is already a superset of it. */
          refresh: false,
          // The server settles the reason outcome itself, so the client does
          // not also write saved_offer — one fact, one writer.
          then: function (st, result) {
            self.state.offer = result || null;
            if (offerJourneyId && self.state.retentionJourneyId === offerJourneyId) {
              self.state.retentionJourneyId = null;
            }
            self.load().then(function () {
              self.show('dashboard');
            }).catch(function () {
              // The write succeeded — only the re-read failed. The discount
              // is real regardless, so this stays a successful action: show
              // the dashboard on whatever state is already held rather than
              // stranding the customer on the offer screen or, worse,
              // turning a successful write into what looks like a failed
              // page.
              self.show('dashboard');
            });
          },
          toast: function (st, result) {
            if (result && result.refreshRequired) {
              return 'Discount applied \u2014 refresh to see your new total.';
            }
            var p = result && result.offerPrice;
            return p
              ? 'Done \u2014 your next delivery is ' + self.fmtMoney({ amount: p, currencyCode: 'USD' })
              : 'Discount applied to your next delivery';
          }
        });
        return;
      }

      /* --- POST /cancel-subscription --- */
      case 'cancel':
        if (!d.reason) { this.show('cancel-reason'); return; }
        this.run('cancel', function (attemptKey) {
          return self.adapter.cancel(id, d.reason, null, { idempotencyKey: attemptKey });
        }, {
          attempt: 'cancel',
          then: function () {
            if (self.adapter.recordCancelOutcome) {
              self.adapter.recordCancelOutcome('cancelled').catch(function () {});
            }
            self.show('cancel-done');
          }
        });
        return;

      /* --- POST /activate-subscription --- */
      case 'reactivate': {
        var offsets = [0, 14, 30];
        this.run('reactivate', function (attemptKey) {
          return self.adapter.reactivate(id, offsets[d.restart] || 0, { idempotencyKey: attemptKey });
        }, {
          attempt: 'reactivate',
          then: function () { self.show('dashboard'); },
          toast: 'Subscription reactivated'
        });
        return;
      }

      /* --- loyalty: request only, never a fulfilment claim --- */
      case 'requestRedemption': {
        var rewardId = el && el.dataset.sppReward;
        if (!rewardId || el.getAttribute('aria-disabled') === 'true') return;
        this.run('requestRedemption', function () { return self.adapter.requestRedemption(rewardId); }, {
          closeSheet: false,
          then: function (st, result) {
            return self.adapter.listRewards().then(function (r) {
              self.state.rewards = r;
              self.render();
              self.toast((result && result.message) ||
                'Your reward request has been received. It will be added to an upcoming delivery.');
            });
          }
        });
        return;
      }

      case 'altPrimary':
        // Only skip or delay can ever be offered here.
        if (this._altAction === 'delay') this.openSheet('delay');
        else this.openSheet('skip');
        return;

      case 'signOut':
        this.adapter.signOut().then(function () { self.show('login'); });
        return;

      case 'support':
      case 'faq':
        // The real, already-public VetPets support address (see the cancel
        // flow's plain contact block) — not a fabricated "message support"
        // channel this codebase has no backend for.
        window.location.href = 'mailto:info@shopvetpets.com';
        return;
    }
  };

  /* =================================================================
   * Init
   * ================================================================= */

  /**
   * Last-resort reveal. If the portal cannot boot at all, the customer must
   * still see something they can act on — never a blank page. This walks the
   * DOM directly rather than going through Portal, because the reason we are
   * here is that Portal could not be constructed.
   */
  function revealFallback(scope, detail) {
    var host = scope || document.querySelector('.spp') || document.body;
    if (!host || host.__sppFallbackShown) return;
    host.__sppFallbackShown = true;

    var error = host.querySelector ? host.querySelector('[data-spp-screen="error"]') : null;
    if (error) {
      // Hide every other screen, then reveal the real error screen so the
      // approved design and its "Try again" affordance are what the customer sees.
      var all = host.querySelectorAll('[data-spp-screen]');
      for (var i = 0; i < all.length; i++) all[i].hidden = true;
      error.hidden = false;
      var ref = error.querySelector('[data-spp-field="error.reference"]');
      if (ref && !ref.textContent) ref.textContent = 'SUB-BOOT';
      return;
    }

    // The markup itself is missing or damaged — inject a minimal notice
    // rather than leaving the page empty.
    var note = document.createElement('div');
    note.setAttribute('role', 'alert');
    note.style.cssText = 'max-width:520px;margin:40px auto;padding:20px;border-radius:14px;' +
      'background:#EEF2F6;border:1px solid rgba(13,35,64,.14);font:500 15px/1.5 system-ui,sans-serif;color:#33445C;';
    note.innerHTML = '<strong style="display:block;color:#0F172A;margin-bottom:6px;">' +
      'We couldn’t load your subscription</strong>' +
      'Nothing has changed on your account. Please refresh, or contact support if it keeps happening.';
    (host.appendChild ? host : document.body).appendChild(note);
    if (window.console && console.error) console.error('[spp] bootstrap failed:', detail || '');
  }

  function init() {
    var roots = document.querySelectorAll('[data-spp-portal]');

    // No root at all. Historically this happened when a Liquid whitespace
    // hyphen welded `data-spp-portal` onto the next attribute, which left the
    // page silently blank. Fail visibly instead.
    if (!roots.length) {
      revealFallback(null, 'no [data-spp-portal] root found');
      return;
    }

    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__sppBooted) continue;
      roots[i].__sppBooted = true;
      try {
        new Portal(roots[i]);
      } catch (e) {
        // Portal itself could not be constructed or could not show a screen.
        revealFallback(roots[i], e && e.message);
      }
    }

    // Belt and braces: if nothing is visible shortly after boot, reveal the
    // error screen rather than leaving the customer looking at nothing.
    setTimeout(function () {
      for (var j = 0; j < roots.length; j++) {
        var visible = roots[j].querySelector('[data-spp-screen]:not([hidden])');
        if (!visible) revealFallback(roots[j], 'no screen visible after init');
      }
    }, 0);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})(window, document);
